import Phaser from 'phaser';
import {
  COINS,
  DamageElement,
  EffectId,
  ITEMS,
  ItemKind,
  ItemRarity,
  ItemSlot,
  SPELLS,
  SkillId,
  TILE_SIZE,
  type ItemType,
} from '@game/protocol';

import { addTexture, context2d, createCanvas, ellipse, outline as strokeSilhouette, px } from './canvas.ts';
import { OUTLINE } from './palette.ts';
import { Material, finish, gem, rarityAura, type Finish, type Ramp } from './rarity.ts';

/**
 * Ícones de item.
 *
 * ## Por que um desenho por item, e não um por categoria
 *
 * A primeira versão desenhava a partir da **categoria** e tirava a cor de um hash do id.
 * Era barato e tinha uma propriedade boa: item novo no catálogo já nascia com aparência
 * própria, sem tocar neste arquivo. Só que elmo, armadura, calça e bota são todos
 * `ItemKind.Armor` — e saíam como o mesmo quadrado em quatro matizes diferentes. No
 * inventário, onde o jogador procura a bota entre vinte quadrados, cor não resolve:
 * ninguém decora que a bota é a lilás. Forma resolve.
 *
 * Agora o desenho vem do **slot** quando existe um — cabeça, tronco, pernas, pés têm
 * silhueta inconfundível — e do id quando a peça é específica. A categoria continua
 * valendo como último recurso, então um item novo ainda aparece sem passar por aqui: ele
 * só não ganha silhueta própria até alguém desenhá-la.
 *
 * A paleta é por **material**, não por item: couro é marrom, malha e lâmina são aço,
 * cabo é madeira. Duas peças do mesmo material devem parecer parentes — é o que permite
 * ler "conjunto de couro" sem ler nome nenhum.
 *
 * ## E o material vem da raridade
 *
 * A regra de cima continua de pé; o que mudou é quem responde "de que material é esta peça".
 * Antes era uma constante escrita dentro de cada função, e o resultado é que a Coroa do
 * dragão ancião — a peça de faixa 8 que cai de um Dragão — saía do mesmo balde de couro
 * marrom do Elmo de couro que qualquer um compra no primeiro balcão. Agora quem responde é
 * `rarity.ts`: comum é couro, raro é aço, épico é o aço-crepúsculo, lendário é ouro de
 * dragão e celestial é o marfim do Alvorecer, e a peça ganha filete e pedra conforme sobe.
 * `MATERIAL` é a única exceção — os poucos itens cujo **nome** já declara a matéria e que a
 * escada erraria (um elmo de aço comum, um manto de pano épico).
 */

const LEATHER = '#8a5f3a';
const LEATHER_DARK = '#5e3f26';
const LEATHER_LIGHT = '#ad7d52';

const STEEL = '#c3cad4';
const STEEL_DARK = '#78818e';
const STEEL_LIGHT = '#e8eef5';

const WOOD = '#7a5533';
const WOOD_DARK = '#513620';

const GOLD = '#d9b13c';
const GOLD_DARK = '#8a6d1f';
const GOLD_LIGHT = '#f2dc8a';

/**
 * Platina: a rampa do degrau do meio da moeda.
 *
 * Não reusa a do aço, apesar de as duas serem metal claro, porque as duas aparecem lado a
 * lado na mesma mochila: aço é o que se veste e platina é o que se gasta, e um azulado a mais
 * na sombra é o bastante para o olho separar uma pilha de moedas de uma placa de armadura sem
 * ler o nome de nenhuma das duas.
 */
const PLATINUM = '#c9d2de';
const PLATINUM_DARK = '#6f7c8d';
const PLATINUM_LIGHT = '#eef3f9';

/**
 * Cristal: o degrau de cima, e o único que precisa de matiz e não só de brilho.
 *
 * Ouro e platina se separam por temperatura — quente e frio —, e o terceiro degrau não tem
 * para onde ir nesse eixo sem virar "platina um pouco mais clara". O verde-água resolve
 * porque muda de **cor** e não de luz: as três pilhas continuam legíveis lado a lado numa
 * mochila, e continuam legíveis para quem não distingue bem o par quente/frio.
 */
const CRYSTAL = '#6ec9cf';
const CRYSTAL_DARK = '#2c7480';
const CRYSTAL_LIGHT = '#c2f0f2';

/**
 * Bronze: o metal das chaves. Não reusa a rampa do ouro de propósito — chave não é
 * tesouro, e o tom mais sujo é o que impede a mochila de prometer moeda onde há
 * fechadura.
 */
const BRONZE = '#b07a35';
const BRONZE_DARK = '#7a5222';
const BRONZE_LIGHT = '#d8a869';

/**
 * Escama de dragão: entre o aço e o verde, como manda a descrição da Placa de escamas.
 * Rampa própria porque não é nem o couro nem o aço — e se um segundo item de escama
 * nascer no catálogo, ele já nasce parente deste.
 */
const SCALE = '#7f9268';
const SCALE_DARK = '#4c5b3c';
const SCALE_LIGHT = '#a9bc88';

/**
 * Comida (PRD §15.7).
 *
 * As rampas de cima são por **material** — couro, aço, madeira — e existem para aproximar peças
 * parentes: é o que deixa ler "conjunto de couro" sem ler nome nenhum. A comida quer o oposto.
 * Os seis alimentos não formam família: eles se diferenciam por quanto alimentam e quanto pesam,
 * e a única decisão que o jogador toma com eles é qual gastar agora. Reaproveitar `LEATHER` no
 * pão e no presunto — que era o barato, já que ambos são marrom-dourados — os jogaria na mesma
 * faixa que já ocupa metade do catálogo, e a escolha de §15.7 viraria clique errado: comer o
 * presunto de 10 minutos achando que era o pão de 2, no meio de uma mochila de quadrados
 * marrons iguais. Por isso cada alimento tem matiz próprio, e nenhum deles reusa os de cima.
 */
const MUSHROOM = '#8f6a90';
const MUSHROOM_DARK = '#5c4160';
const MUSHROOM_LIGHT = '#b088ae';
const MUSHROOM_STEM = '#e6dcc4';
const MUSHROOM_STEM_DARK = '#bcae94';

const BREAD = '#b9762f';
const BREAD_DARK = '#8a5420';
const BREAD_LIGHT = '#d4914a';
const BREAD_CRUMB = '#f0d69a';

const CHEESE = '#e8c33c';
const CHEESE_DARK = '#a17a18';
const CHEESE_LIGHT = '#f7e493';

const MEAT = '#bf4038';
const MEAT_DARK = '#7c2621';
const MEAT_LIGHT = '#d9736a';
const FAT = '#f2e6c8';

const HAM = '#e08d97';
const HAM_DARK = '#a85a68';
const HAM_LIGHT = '#f4b7bd';

const FISH = '#7fa8c6';
const FISH_DARK = '#456f92';
const FISH_LIGHT = '#cbe0ef';

/** Osso: o mesmo tom da presa de troll, porque osso de bicho abatido é a mesma matéria. */
const BONE = '#efe7d2';
const BONE_DARK = '#c9bfa4';

export function itemTextureKey(typeId: number): string {
  return `item-${typeId}`;
}

export function buildItemIcons(scene: Phaser.Scene): void {
  for (const type of Object.values(ITEMS)) {
    const key = itemTextureKey(type.id);
    if (scene.textures.exists(key)) continue;

    addTexture(scene, key, itemIcon(type));
  }
}

/**
 * O ícone de um item, pronto e fora do Phaser.
 *
 * Separado de `buildItemIcons` porque a prévia de `items-preview.ts` desenha o catálogo
 * inteiro numa página sem cena, e a alternativa seria uma segunda cópia deste desenho —
 * que é exatamente o arranjo que deixa a prévia e o jogo divergirem sem ninguém notar.
 */
export function itemIcon(type: ItemType): HTMLCanvasElement {
  const canvas = createCanvas(TILE_SIZE, TILE_SIZE);
  const ctx = context2d(canvas, true);

  drawItem(ctx, type);
  // Por último, e por fora do desenho: ver `rarityAura`.
  rarityAura(ctx, type.rarity);

  return canvas;
}

/**
 * Matéria declarada, para os itens em que a raridade sozinha erraria.
 *
 * Curto de propósito. Cada linha aqui é uma peça cujo **nome** promete uma matéria que a
 * escada de raridade não daria: o Elmo de aço é comum e sairia de couro, o Manto do
 * conjurador é pano e sairia de placa. Fora destes, `Material.Hide` é o padrão e a raridade
 * decide — que é o que impede esta tabela de crescer até virar o "um desenho por item" que o
 * cabeçalho deste arquivo recusa.
 */
const MATERIAL: Readonly<Record<number, Material>> = {
  252: Material.Metal, // Brigantina
  261: Material.Metal, // Elmo de aço
  271: Material.Metal, // Perneiras de malha
  291: Material.Metal, // Escudo de aço

  250: Material.Cloth, // Túnica acolchoada
  257: Material.Cloth, // Manto do conjurador
  258: Material.Cloth, // Manto rúnico
  259: Material.Cloth, // Manto das constelações
  260: Material.Cloth, // Capuz de pano
  266: Material.Cloth, // Tiara de mago
  267: Material.Cloth, // Diadema estelar
  270: Material.Cloth, // Calças de pano
  275: Material.Cloth, // Saiote rúnico
  280: Material.Cloth, // Sapatos de pano
  285: Material.Cloth, // Sandálias do conjurador
  903: Material.Cloth, // Manto do Alvorecer
};

/**
 * As peças de pano que são **manto**, e não túnica.
 *
 * O corte é outro: o manto cai até os pés e tem gola alta, e é essa silhueta que separa o
 * conjurador do guerreiro na mochila — o mesmo trabalho que a calça e a bota fazem entre si.
 * A Túnica acolchoada fica de fora porque gambeson é peitoral, não manto.
 */
const ROBES = new Set([257, 258, 259, 903]);

/** Coroa: pontas e três pedras. As duas peças de cabeça que o nome chama de coroa. */
const CROWNS = new Set([265, 902]);

/** Aro fino com uma pedra só. Tiara e diadema — o oposto do elmo, que esconde o rosto. */
const CIRCLETS = new Set([266, 267]);

/**
 * De que a peça é feita.
 *
 * O slot responde quase tudo de graça: o que se empunha, se calça no dedo ou se atira é
 * metal, e o que se veste é couro até a raridade dizer o contrário. `MATERIAL` só entra
 * onde essa regra erraria — e a ordem importa, porque é a exceção que precisa vencer.
 */
function materialOf(type: ItemType): Material {
  const declared = MATERIAL[type.id];
  if (declared !== undefined) return declared;

  switch (type.slot) {
    case ItemSlot.Weapon:
    case ItemSlot.Ammo:
    case ItemSlot.Amulet:
    case ItemSlot.Ring:
      return Material.Metal;
    default:
      return type.kind === ItemKind.Shield ? Material.Metal : Material.Hide;
  }
}

/** Matiz estável a partir do id, para o que ainda não tem desenho próprio. */
function color(typeId: number, lightness: number, saturation = 45): string {
  return `hsl(${(typeId * 47) % 360}, ${saturation}%, ${lightness}%)`;
}

function drawItem(ctx: CanvasRenderingContext2D, type: ItemType): void {
  const cx = TILE_SIZE / 2;
  const cy = TILE_SIZE / 2;
  const it = finish(type.rarity, materialOf(type));

  // Peças específicas primeiro: são as que o jogador vê o tempo todo.
  switch (type.id) {
    case 20:
      drawSword(ctx, cx, cy, 15, STEEL, WOOD, it);
      return outline(ctx);
    case 23:
      drawSword(ctx, cx, cy, 20, STEEL_LIGHT, GOLD, it);
      return outline(ctx);
    case 21:
      drawAxe(ctx, cx, cy, it);
      return outline(ctx);
    case 22:
      drawClub(ctx, cx, cy, it);
      return outline(ctx);
    // Os dois bastões iniciais. Mesma silhueta, pedra do elemento: ver `elementStone`.
    case 24:
    case 25:
      drawRod(ctx, cx, cy, it, elementStone(type.element));
      return outline(ctx);
    case 30:
      drawBow(ctx, cx, cy, it);
      return outline(ctx);
    case 31:
      drawArrow(ctx, cx, cy, it);
      return outline(ctx);
    case 41:
    // A Cota de malha de faixa 4 (101) tem o mesmo nome e a mesma matéria da 41: cair
    // no `drawArmor` do slot a vestiria de couro marrom — infiel ao nome e fora da
    // família de aço. Mesma função de propósito: os números mudam, a malha não.
    case 101:
      drawMail(ctx, cx, cy, it);
      return outline(ctx);
    case 60:
      drawPelt(ctx, cx, cy);
      return outline(ctx);
    case 61:
      drawTusk(ctx, cx, cy);
      return outline(ctx);
    case 62:
      drawTail(ctx, cx, cy);
      return outline(ctx);
    case 71:
      // O corpo apodrecido tem categoria `Corpse` como o fresco, então sem este caso ele sairia
      // pelo mesmo `drawCorpse` e os dois ficariam idênticos — que é exatamente o defeito que
      // §15.7 quer curar: sem a dica visual, todo desaparecimento vira acusação de roubo no
      // chat, porque o jogador não distingue "acabou o tempo" de "alguém pegou".
      drawCorpse(ctx, cx, cy, CORPSE_ROTTEN);
      return outline(ctx);
    case 80:
      // A runa em branco é a mesma pedra sem a inscrição acesa: é o que faz o jogador ver
      // que ela é matéria-prima da outra, e não um item sem relação.
      drawRune(ctx, cx, cy, null);
      return outline(ctx);

    // As cinco ferramentas e a chave entram por id porque a categoria não tem desenho: o
    // terceiro switch as jogava na bolha genérica, e a ferramenta é justamente o item que
    // o jogador procura na mochila parado na frente do obstáculo — é a forma que ele
    // encontra, não o nome.
    case 91:
      drawShovel(ctx, cx, cy);
      return outline(ctx);
    case 92:
      drawPickaxe(ctx, cx, cy);
      return outline(ctx);
    case 93:
      drawLumberAxe(ctx, cx, cy);
      return outline(ctx);
    case 94:
      drawKnife(ctx, cx, cy);
      return outline(ctx);
    case 95:
      drawFishingRod(ctx, cx, cy);
      return outline(ctx);
    case 97:
      drawKey(ctx, cx, cy);
      return outline(ctx);

    // O equipamento de faixa alta (PRD §15.5): são as peças que a raridade promete serem
    // diferentes, e que caíam na mesma bolha dos itens sem desenho — o loot raro nascia
    // invisível no exato momento em que deveria dizer "isto é diferente".
    case 100:
      // Aço dos pés à cabeça: lâmina mais clara e guarda de metal, um degrau visível
      // acima da Espada curta (20) e sem o ouro da longa (23).
      drawSword(ctx, cx, cy, 18, STEEL_LIGHT, STEEL, it);
      return outline(ctx);
    case 102:
      drawWarAxe(ctx, cx, cy, it);
      return outline(ctx);
    case 103:
      drawScaleArmor(ctx, cx, cy);
      return outline(ctx);
    case 104:
      drawRunicStaff(ctx, cx, cy, it);
      return outline(ctx);
    case 110:
      drawAmulet(ctx, cx, cy, it);
      return outline(ctx);
    case 111:
      drawRing(ctx, cx, cy, it);
      return outline(ctx);

    // A comida entra por id, e não por `ItemKind.Food` no terceiro estágio, porque uma função
    // por categoria só teria o id para variar — e variar cor por hash devolveria seis manchas
    // do mesmo formato. §15.7 pede que o jogador escolha **qual** comer pelo que ela alimenta e
    // pesa; com seis quadrados iguais em matizes sorteados ele escolhe pela posição na mochila,
    // e come o presunto de dez minutos no lugar do cogumelo de um.
    case 96:
      drawFish(ctx, cx, cy);
      return outline(ctx);
    case 120:
      drawMushroom(ctx, cx, cy);
      return outline(ctx);
    case 121:
      drawBread(ctx, cx, cy);
      return outline(ctx);
    case 122:
      drawCheese(ctx, cx, cy);
      return outline(ctx);
    case 123:
      drawMeat(ctx, cx, cy);
      return outline(ctx);
    case 124:
      drawHam(ctx, cx, cy);
      return outline(ctx);

    // --- O que §15.11 acrescentou e que **já tinha desenho** ---
    //
    // Nenhum destes ganhou função nova: são ids novos apontando para as silhuetas que já
    // existiam. É a escolha barata e é a certa — a costela é carne, o peixe assado é peixe,
    // a foice é uma lâmina de cabo comprido como o machado de lenhador. Desenhar um ícone
    // próprio para cada um seria escrever vinte desenhos que ninguém distinguiria a 32 px.
    case 348:
      drawMeat(ctx, cx, cy);
      return outline(ctx);
    // Sopa e torta saíam como a mesma lasca de carne da Costela assada, e as três estão na
    // mesma prateleira da mochila: a sopa é a que alimenta pouco e a torta é a cara. Um
    // ícone que não separa as duas devolve o defeito que §15.7 tinha curado na comida.
    case 345:
      drawSoup(ctx, cx, cy);
      return outline(ctx);
    case 347:
      drawPie(ctx, cx, cy);
      return outline(ctx);
    case 349:
      drawHam(ctx, cx, cy);
      return outline(ctx);
    case 344:
      drawBread(ctx, cx, cy);
      return outline(ctx);
    case 346:
      drawFish(ctx, cx, cy);
      return outline(ctx);
    case 350:
      drawPelt(ctx, cx, cy);
      return outline(ctx);
    case 351:
    case 353:
    case 356:
    case 364:
      drawTusk(ctx, cx, cy);
      return outline(ctx);
    case 413:
      drawLumberAxe(ctx, cx, cy);
      return outline(ctx);
    case 414:
      drawFishingRod(ctx, cx, cy);
      return outline(ctx);

    // --- O que estava na bolha genérica ---
    //
    // Dezessete itens saíam daqui como uma bola de matiz sorteado pelo id, e a defesa disso
    // era que "uma bolha para matéria-prima é honesta". Não era. Quatro alimentos caíam na
    // bolha ao lado dos seis que §15.7 desenhou justamente para não se confundirem, e as
    // quatro essências elementais — que só existem para o jogador **separar** terra de fogo
    // no despojo — saíam como quatro bolas cuja única diferença era um matiz que ninguém
    // decora. A matéria-prima é o item que se olha em maior quantidade no jogo: ela é o que
    // enche a mochila depois de uma tarde de caça.
    case 340:
      drawApple(ctx, cx, cy);
      return outline(ctx);
    case 341:
      drawCarrot(ctx, cx, cy);
      return outline(ctx);
    case 342:
      drawEgg(ctx, cx, cy);
      return outline(ctx);
    case 343:
      drawGrapes(ctx, cx, cy);
      return outline(ctx);
    case 352:
      drawHoof(ctx, cx, cy);
      return outline(ctx);
    case 354:
      drawSilk(ctx, cx, cy);
      return outline(ctx);
    case 355:
      drawBatWing(ctx, cx, cy);
      return outline(ctx);
    case 357:
      drawSkull(ctx, cx, cy);
      return outline(ctx);

    // As quatro essências: o mesmo cristal, e só a cor muda. A família é o ponto — quem
    // reconhece uma reconhece as quatro —, e a cor é a mesma que a magia do elemento já
    // pinta na tela, que é o vocabulário que o jogador aprendeu apanhando dela.
    case 358:
      drawEssence(ctx, cx, cy, ESSENCE_EARTH);
      return outline(ctx);
    case 359:
      drawEssence(ctx, cx, cy, ESSENCE_FIRE);
      return outline(ctx);
    case 360:
      drawEssence(ctx, cx, cy, ESSENCE_ICE);
      return outline(ctx);
    case 361:
      drawEssence(ctx, cx, cy, ESSENCE_ENERGY);
      return outline(ctx);

    case 362:
      drawScale(ctx, cx, cy, SCALE, SCALE_DARK, SCALE_LIGHT);
      return outline(ctx);
    case 363:
      // A escama de Dragão é a mesma peça em brasa: mesma forma, outra têmpera. É o que diz
      // que as duas vêm da mesma linhagem sem que nenhuma delas precise de nome.
      drawScale(ctx, cx, cy, '#a4472f', '#68271b', '#d4795a');
      return outline(ctx);
    case 365:
      drawHeart(ctx, cx, cy);
      return outline(ctx);
    case 366:
      drawDust(ctx, cx, cy);
      return outline(ctx);
    case 906:
      drawDawnShard(ctx, cx, cy);
      return outline(ctx);

    default:
      break;
  }

  // Depois o slot: é ele que diz onde a peça é vestida, e a silhueta segue disso.
  switch (type.slot) {
    case ItemSlot.Head:
      // Três silhuetas para a mesma cabeça, e não uma calota para todas: a coroa e o elmo
      // fechado são objetos opostos — um mostra o rosto e o outro o esconde —, e o capuz é
      // pano onde os dois são casca. Com uma silhueta só, a Coroa do dragão ancião e o Elmo
      // de couro eram o mesmo desenho pintado de outra cor.
      if (CROWNS.has(type.id)) drawCrown(ctx, cx, cy, it);
      else if (CIRCLETS.has(type.id)) drawCirclet(ctx, cx, cy, it);
      else if (MATERIAL[type.id] === Material.Cloth) drawHood(ctx, cx, cy, it);
      else drawHelmet(ctx, cx, cy, it);
      return outline(ctx);
    case ItemSlot.Legs:
      drawLegs(ctx, cx, cy, it);
      return outline(ctx);
    case ItemSlot.Feet:
      // Sapato de pano e sandália não têm cano, e é o cano que faz a bota ser bota.
      if (MATERIAL[type.id] === Material.Cloth) drawShoes(ctx, cx, cy, it);
      else drawBoots(ctx, cx, cy, it);
      return outline(ctx);
    case ItemSlot.Armor:
      if (ROBES.has(type.id)) drawRobe(ctx, cx, cy, it);
      else drawArmor(ctx, cx, cy, it);
      return outline(ctx);

    // Os quatro slots que faltavam, e o catálogo de §15.11 é quem cobrou a conta: a mão
    // direita passou de nove armas para trinta e quatro, o pescoço e o dedo de duas peças
    // para onze, e a munição de uma para seis. Todas caíam na bolha genérica do fim desta
    // função — trinta e quatro bolhas de matiz sorteado, que é exatamente o defeito que o
    // cabeçalho deste arquivo descreve ao explicar por que o desenho **não** vem de um hash
    // do id: no inventário, cor não resolve, forma resolve.
    case ItemSlot.Weapon:
      drawWeapon(ctx, cx, cy, type, it);
      return outline(ctx);
    case ItemSlot.Ammo:
      drawArrow(ctx, cx, cy, it);
      return outline(ctx);
    case ItemSlot.Amulet:
      drawAmulet(ctx, cx, cy, it);
      return outline(ctx);
    case ItemSlot.Ring:
      drawRing(ctx, cx, cy, it);
      return outline(ctx);
    default:
      break;
  }

  switch (type.kind) {
    case ItemKind.Currency:
      drawCoins(ctx, cx, cy, coinRamp(type));
      break;

    case ItemKind.Shield:
      // A face de metal começa no raro, e o Escudo de aço é a exceção que o nome cobra: ele
      // é comum e mesmo assim não pode sair de tábua.
      drawShield(ctx, cx, cy, it, MATERIAL[type.id] === Material.Metal || it.rarity >= ItemRarity.Rare);
      break;

    case ItemKind.Container:
      drawBackpack(ctx, cx, cy, type.capacity ?? 8, it);
      break;

    case ItemKind.Corpse:
      drawCorpse(ctx, cx, cy, CORPSE_FRESH);
      break;

    case ItemKind.Rune:
      drawRune(ctx, cx, cy, runeGlow(type));
      break;

    case ItemKind.Consumable:
      drawPotion(ctx, cx, cy, type, it);
      break;

    default:
      ellipse(ctx, cx, cy, 8, 8, color(type.id, 45));
      ellipse(ctx, cx - 2, cy - 2, 3, 3, color(type.id, 65));
  }

  outline(ctx);
}

/**
 * A arma, desenhada a partir da **perícia** dela.
 *
 * É o único campo do catálogo que separa uma espada de um machado sem que alguém escreva um
 * `case` por item: `skill` já existe porque o servidor precisa dela para calcular o dano, e
 * uma espada nova entra no jogo com silhueta de espada sem tocar neste arquivo. É a mesma
 * ideia do slot — usar o dado que a peça **já** declara em vez de uma tabela paralela que
 * envelhece —, e é o que torna o catálogo de §15.11 sustentável: as vinte e cinco armas novas
 * custaram esta função, e não vinte e cinco desenhos.
 *
 * O `attack` escolhe entre o desenho pequeno e o grande da mesma família — machadinha e
 * machado do trovão são o mesmo gesto em escalas diferentes —, e a raridade escolhe o metal.
 * Nenhum dos dois inventa forma nova: quem quiser silhueta própria continua escrevendo o
 * `case` por id lá em cima, que é onde as nove armas desenhadas à mão estão.
 */
function drawWeapon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  type: ItemType,
  it: Finish,
): void {
  switch (type.skill) {
    case SkillId.Axe:
      // O machado grande a partir do ponto em que ele deixa de ser de uma mão só. 26 é o
      // degrau entre a Alabarda (27) e a Maça (22), que é onde a família muda de porte.
      if ((type.attack ?? 0) >= 26) drawWarAxe(ctx, cx, cy, it);
      else drawAxe(ctx, cx, cy, it);
      return;

    case SkillId.Club:
      drawClub(ctx, cx, cy, it);
      return;

    case SkillId.Magic:
      // O cajado rúnico tem gema; a varinha e o bastão de carvalho, não. O corte é o mesmo
      // do Cajado de cristal (16), a primeira peça da linha que a descrição chama de rúnica.
      if ((type.attack ?? 0) >= 16) drawRunicStaff(ctx, cx, cy, it);
      else drawRod(ctx, cx, cy, it, elementStone(type.element));
      return;

    case SkillId.Distance:
      drawBow(ctx, cx, cy, it);
      return;

    default:
      drawSword(ctx, cx, cy, bladeLength(type), ...bladeMetal(it), it);
  }
}

/** Lâmina mais longa para arma mais forte, dentro do que cabe nos 32 px do ícone. */
function bladeLength(type: ItemType): number {
  return Math.min(22, 14 + Math.floor((type.attack ?? 0) / 6));
}

/**
 * O par lâmina/punho por raridade.
 *
 * A borda colorida do slot já diz a raridade, e isto é o **reforço** dela dentro do ícone:
 * quem joga com a janela pequena lê a peça antes de ler a borda de um pixel. O punho de
 * madeira só existe no comum — de `Uncommon` para cima ele é o filete de metal nobre, e de
 * `Epic` para cima a lâmina inteira muda de têmpera junto com o resto da peça.
 */
function bladeMetal(it: Finish): [blade: string, hilt: string] {
  // O punho lendário é o **ouro escuro** da própria peça, e não o filete claro: com os dois
  // no mesmo tom, a Espada matadragões saía como uma barra amarela sem guarda visível. A
  // celestial volta ao ouro porque a lâmina dela é marfim, e ouro sobre marfim separa.
  if (it.rarity >= ItemRarity.Celestial) return [it.ramp.light, GOLD];
  if (it.rarity >= ItemRarity.Legendary) return [it.ramp.light, it.ramp.dark];
  if (it.rarity >= ItemRarity.Epic) return [it.ramp.light, GOLD];
  if (it.rarity >= ItemRarity.Uncommon) return [STEEL_LIGHT, STEEL];
  return [STEEL, WOOD];
}

// --- Armas -------------------------------------------------------------------

/** Espada: lâmina com nervura central, guarda cruzada, punho e pomo. */
function drawSword(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  length: number,
  blade: string,
  hilt: string,
  it: Finish,
): void {
  const tip = cy + 8 - length;
  // A nervura acompanha a lâmina de `Epic` para cima: com a nervura sempre de aço, a Lâmina
  // do Alvorecer saía marfim com um risco cinza no meio, que lê como remendo.
  const spine = metalOr(it, STEEL_RAMP);

  px(ctx, cx - 2, tip + 2, 5, length - 2, blade);
  // Ponta em cunha: dois pixels que estreitam a lâmina e a fazem parecer afiada.
  px(ctx, cx - 1, tip, 3, 3, blade);
  px(ctx, cx, tip - 1, 1, 2, blade);
  // Nervura clara à esquerda, sombra à direita: a luz do jogo vem do noroeste.
  px(ctx, cx - 2, tip + 2, 1, length - 2, spine.light);
  px(ctx, cx + 2, tip + 2, 1, length - 2, spine.dark);

  px(ctx, cx - 6, cy + 7, 13, 3, hilt);
  px(ctx, cx - 6, cy + 7, 13, 1, '#ffffff44');
  px(ctx, cx - 1, cy + 10, 3, 5, it.rarity >= ItemRarity.Epic ? it.ramp.dark : WOOD_DARK);
  px(ctx, cx - 2, cy + 14, 5, 2, hilt);

  // A pedra no pomo, que é o ponto da espada que a mão não cobre e o único lugar onde ela
  // cabe sem disputar espaço com a guarda.
  gem(ctx, cx, cy + 15, 2, it);
}

/** Machado: cabeça em meia-lua de um lado só, cabo de madeira. */
function drawAxe(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const head = metalOr(it, STEEL_RAMP);

  px(ctx, cx - 1, cy - 12, 3, 26, WOOD);
  px(ctx, cx - 1, cy - 12, 1, 26, '#8f6640');

  ctx.fillStyle = head.base;
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 11);
  ctx.quadraticCurveTo(cx + 13, cy - 8, cx + 12, cy + 1);
  ctx.lineTo(cx + 1, cy - 1);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = head.light;
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 11);
  ctx.quadraticCurveTo(cx + 11, cy - 8, cx + 10, cy - 4);
  ctx.lineTo(cx + 1, cy - 6);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx - 3, cy - 12, 7, 3, head.dark);
  px(ctx, cx - 3, cy + 10, 7, 2, WOOD_DARK);
  if (it.trim) px(ctx, cx - 2, cy + 2, 5, 2, it.trim);
  gem(ctx, cx, cy + 6, 2, it);
}

const STEEL_RAMP: Ramp = { base: STEEL, dark: STEEL_DARK, light: STEEL_LIGHT };
const GOLD_RAMP: Ramp = { base: GOLD, dark: GOLD_DARK, light: GOLD_LIGHT };
const LEATHER_RAMP: Ramp = { base: LEATHER, dark: LEATHER_DARK, light: LEATHER_LIGHT };

/**
 * A rampa da peça, com um piso.
 *
 * A raridade só passa a mandar na matéria a partir do épico, e abaixo disso cada peça tem a
 * sua de fábrica: a cabeça do machado é aço, o aro do anel é ouro, o corpo da mochila é
 * couro. Sem esse piso, um anel comum sairia de couro marrom só porque o couro é o que a
 * escada dá no degrau de baixo.
 *
 * Repare no que **não** entra aqui: o cabo de madeira do machado, que não sobe nunca. Se ele
 * virasse ouro junto com a cabeça, o Machado do trovão deixaria de ser um machado e viraria
 * uma barra amarela — a família tem de continuar reconhecível depois de enfeitada.
 */
function metalOr(it: Finish, common: Ramp): Ramp {
  return it.rarity >= ItemRarity.Epic ? it.ramp : common;
}

/**
 * Clava e marreta: o mesmo cabo, e a cabeça muda de natureza com a faixa.
 *
 * Até o raro é um tronco grosso com cravos, que é o que se dá a um bandido de estrada. De
 * `Epic` para cima o nome promete outra coisa — Martelo do juízo, Marreta do titã —, e a
 * cabeça vira **massa de metal deitada**, mais larga que alta.
 *
 * A largura é a decisão, e ela custou uma tentativa. Trocar só a tinta do tronco por ouro
 * devolvia um retângulo alto e amarelo, que a 32 px não é uma marreta: é uma barra. O que
 * diz "isto esmaga" é a proporção — cabeça larga, cabo estreito — e as duas faces de
 * percussão nas pontas, que são o detalhe que nenhuma outra arma do catálogo tem.
 */
function drawClub(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const head = metalOr(it, STEEL_RAMP);
  px(ctx, cx - 2, cy + 2, 5, 12, WOOD_DARK);
  px(ctx, cx - 3, cy + 13, 7, 2, WOOD_DARK);

  if (it.rarity >= ItemRarity.Epic) {
    // Cabo passando por dentro da cabeça, para a peça sair inteiriça em vez de empilhada.
    px(ctx, cx - 2, cy - 10, 5, 14, WOOD);
    px(ctx, cx - 2, cy - 10, 1, 14, '#8f6640');

    px(ctx, cx - 11, cy - 10, 23, 10, head.base);
    px(ctx, cx - 11, cy - 10, 23, 2, head.light);
    px(ctx, cx - 11, cy - 2, 23, 2, head.dark);
    // Faces de percussão: as duas pontas em tom separado, que é o que faz o bloco ter
    // volume e não parecer uma etiqueta colada no cabo.
    px(ctx, cx - 11, cy - 10, 4, 10, head.dark);
    px(ctx, cx + 8, cy - 10, 4, 10, head.dark);
    px(ctx, cx - 11, cy - 10, 4, 2, head.light);

    // Colar entre a cabeça e o cabo.
    px(ctx, cx - 4, cy, 9, 3, head.dark);
    if (it.trim) px(ctx, cx - 4, cy, 9, 1, it.trim);
    gem(ctx, cx, cy - 5, 3, it);
    return;
  }

  px(ctx, cx - 4, cy - 12, 9, 15, WOOD);
  px(ctx, cx - 4, cy - 12, 3, 15, '#8f6640');
  px(ctx, cx - 4, cy - 13, 9, 2, WOOD_DARK);

  for (const [x, y] of [
    [-3, -9],
    [2, -6],
    [-2, -2],
    [3, -11],
  ] as const) {
    px(ctx, cx + x, cy + y, 2, 2, STEEL_DARK);
    px(ctx, cx + x, cy + y, 1, 1, STEEL_LIGHT);
  }

  if (it.trim) px(ctx, cx - 3, cy + 3, 7, 2, it.trim);
  gem(ctx, cx, cy - 5, 2.5, it);
}

/**
 * A pedra que o elemento da arma pede, em três tons: aro, miolo e brilho.
 *
 * Existe porque os dois bastões iniciais são a **mesma peça** em tudo que o desenho sabe
 * ler — mesma raridade, mesmo ataque, mesma silhueta — e sairiam como dois ícones idênticos
 * na mochila. O elemento é a única coisa que os separa em jogo, então é ele que tem que
 * separá-los na tela, e num ícone de 32 px a cor da gema é o canal que sobra.
 *
 * Sai de `type.element` e não do id, pelo mesmo motivo que `drawWeapon` despacha por
 * `skill`: um terceiro bastão elemental deve nascer com a pedra certa sem tocar nesta
 * função. `null` para o que não declara elemento — que é quase todo o catálogo, e é onde a
 * pedra volta a ser a da raridade.
 */
function elementStone(
  element: DamageElement | undefined,
): readonly [rim: string, core: string, glint: string] | null {
  switch (element) {
    case DamageElement.Fire:
      return ['#8f2a10', '#e8632a', '#ffd8a8'];
    case DamageElement.Ice:
      return ['#2f6f9e', '#7fd0f0', '#eaf8ff'];
    case DamageElement.Energy:
      return ['#4a2f9e', '#9b7bf0', '#e6dcff'];
    case DamageElement.Earth:
      return ['#3f6a2a', '#7fbf4f', '#e0f5c8'];
    default:
      return null;
  }
}

/**
 * Bastão: haste fina e reta com uma gema acesa no topo.
 *
 * A silhueta precisa se separar da clava, que é a outra peça de madeira do catálogo, e a
 * diferença está na proporção: a clava engrossa para cima e o bastão não engrossa nunca.
 * A gema é o que diz "isto é mágico" sem nome nenhum — e é o único ponto de cor fria entre
 * armas que são todas aço e madeira.
 */
function drawRod(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  it: Finish,
  stone: readonly [rim: string, core: string, glint: string] | null = null,
): void {
  px(ctx, cx - 1, cy - 6, 3, 21, WOOD);
  px(ctx, cx - 1, cy - 6, 1, 21, '#8f6640');
  px(ctx, cx - 2, cy + 13, 5, 2, WOOD_DARK);

  // Enrolamento de couro no punho: quebra a coluna lisa e marca onde a mão pega.
  for (const y of [4, 7, 10]) {
    px(ctx, cx - 2, cy + y, 5, 1, LEATHER_DARK);
  }

  // Garra de metal segurando a gema. Sem ela a pedra parece flutuar sobre a haste.
  px(ctx, cx - 3, cy - 8, 7, 3, it.trim ? it.ramp.dark : STEEL_DARK);
  px(ctx, cx - 3, cy - 8, 7, 1, it.trim ?? STEEL);

  // A pedra da varinha é a raridade quando existe uma: aqui ela não é enfeite ao lado do
  // desenho, é o próprio desenho — e uma varinha rara com a mesma pedra azul da de aprendiz
  // desperdiça o único ponto de cor que a peça tem.
  //
  // O elemento ganha da raridade quando os dois falam: as duas peças que hoje o declaram são
  // comuns, e a raridade não tem pedra nenhuma para elas — mas se um bastão de fogo épico
  // existir um dia, "de fogo" é o que o jogador precisa ler para escolher o alvo, e a borda
  // colorida do slot já diz o resto.
  const [rim, core, glint] = stone ??
    (it.gem ? [it.ramp.dark, it.gem, it.gemLight] : ['#3d6fb5', '#5f9be0', '#d8ecff']);

  ellipse(ctx, cx, cy - 12, 5, 5, rim);
  ellipse(ctx, cx, cy - 12, 3.5, 3.5, core);
  ellipse(ctx, cx - 1, cy - 13, 1.6, 1.6, glint);
}

/** Arco: arco de madeira com corda tensionada. Silhueta inconfundível. */
function drawBow(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  ctx.strokeStyle = it.rarity >= ItemRarity.Epic ? it.ramp.base : WOOD;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx + 3, cy, 11, Math.PI * 0.58, Math.PI * 1.42);
  ctx.stroke();

  ctx.strokeStyle = it.rarity >= ItemRarity.Epic ? it.ramp.light : '#8f6640';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx + 4, cy, 11, Math.PI * 0.58, Math.PI * 1.42);
  ctx.stroke();

  px(ctx, cx + 5, cy - 10, 1, 20, '#e8e2cf');
  // As pontas do arco: madeira crua no comum, metal lavrado quando a peça sobe.
  const tips = it.trim ?? WOOD_DARK;
  px(ctx, cx - 8, cy - 10, 3, 3, tips);
  px(ctx, cx - 8, cy + 8, 3, 3, tips);
  gem(ctx, cx - 3, cy, 2, it);
}

/** Flecha: haste, ponta de aço e empenagem. */
function drawArrow(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  // A ponta é o que muda: a Flecha de fogo, a perfurante e o Virote sagrado são a mesma
  // haste com outra cabeça, e cabeça é o que o olho pega numa munição de 32 px.
  const head = it.rarity >= ItemRarity.Uncommon ? it.ramp : STEEL_RAMP;

  px(ctx, cx - 1, cy - 8, 2, 18, WOOD);
  px(ctx, cx - 2, cy - 13, 4, 5, head.base);
  px(ctx, cx - 1, cy - 15, 2, 3, head.light);
  px(ctx, cx - 4, cy + 7, 3, 5, '#d8d2bf');
  px(ctx, cx + 1, cy + 7, 3, 5, '#b9b2a0');
  if (it.gem) px(ctx, cx - 1, cy - 11, 2, 2, it.gem);
}

// --- Proteção ----------------------------------------------------------------

/** Elmo: calota com abertura para o rosto e protetor de nariz. */
function drawHelmet(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  ellipse(ctx, cx, cy - 1, 10, 10, base);
  ellipse(ctx, cx - 3, cy - 5, 6, 4, light);
  px(ctx, cx - 10, cy - 1, 20, 4, dark);

  // Abertura do rosto: o vazio escuro é o que faz o objeto virar elmo, e não tigela.
  px(ctx, cx - 6, cy + 2, 12, 6, '#1a1410');
  px(ctx, cx - 1, cy + 2, 3, 6, base);
  px(ctx, cx - 8, cy + 7, 16, 3, dark);

  // Aro da testa e a pedra sobre o nasal: é onde a peça de faixa alta gasta o enfeite dela,
  // e é o ponto que sobra livre num elmo de 32 px.
  if (it.trim) px(ctx, cx - 10, cy - 1, 20, 1, it.trim);
  gem(ctx, cx, cy - 3, 2.5, it);
}

/** Armadura: peitoral com ombreiras e cinto. A matéria vem da raridade — ver `rarity.ts`. */
function drawArmor(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  px(ctx, cx - 7, cy - 8, 15, 17, base);
  px(ctx, cx - 7, cy - 8, 5, 17, light);
  px(ctx, cx + 4, cy - 8, 4, 17, dark);

  // Ombreiras: dois blocos que passam da linha do tronco. É o que diz "isto se veste".
  px(ctx, cx - 11, cy - 7, 5, 7, dark);
  px(ctx, cx + 7, cy - 7, 5, 7, dark);
  px(ctx, cx - 11, cy - 7, 5, 2, light);

  // Gola e cinto.
  px(ctx, cx - 4, cy - 10, 9, 3, dark);
  px(ctx, cx - 7, cy + 5, 15, 3, it.trim ? it.ramp.dark : '#4a3020');
  px(ctx, cx - 1, cy + 5, 3, 3, it.trim ?? GOLD);

  // Filete nas ombreiras e a pedra no peito, no lugar em que uma couraça de verdade põe o
  // brasão: acima do cinto e abaixo da gola, que é o único vão livre do peitoral.
  if (it.trim) {
    px(ctx, cx - 11, cy - 8, 5, 1, it.trim);
    px(ctx, cx + 7, cy - 8, 5, 1, it.trim);
  }
  gem(ctx, cx, cy - 2, 3, it);
}

/** Cota de malha: mesmo corte da armadura, em aço e com a trama visível. */
function drawMail(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  px(ctx, cx - 7, cy - 8, 15, 17, STEEL_DARK);
  px(ctx, cx - 11, cy - 7, 5, 7, STEEL_DARK);
  px(ctx, cx + 7, cy - 7, 5, 7, STEEL_DARK);

  // Anéis em fiadas desencontradas: dois pixels claros por anel bastam para o olho ler
  // trama em vez de chapa.
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const x = cx - 7 + col * 2 + (row % 2);
      const y = cy - 8 + row * 2;
      if (x >= cx + 8) continue;
      px(ctx, x, y, 1, 1, row % 2 === 0 ? STEEL : STEEL_LIGHT);
    }
  }

  px(ctx, cx - 4, cy - 10, 9, 3, it.trim ?? STEEL);
  px(ctx, cx - 7, cy + 5, 15, 2, '#4a4d55');
}

/** Calças: duas pernas com um vão entre elas — a silhueta que ninguém confunde. */
function drawLegs(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;
  const shadow = it.rarity >= ItemRarity.Rare ? STEEL_DARK : '#3a2616';

  px(ctx, cx - 8, cy - 9, 17, 6, base);
  px(ctx, cx - 8, cy - 9, 17, 2, light);
  px(ctx, cx - 8, cy - 4, 7, 14, base);
  px(ctx, cx + 2, cy - 4, 7, 14, dark);
  px(ctx, cx - 8, cy - 4, 2, 14, light);
  px(ctx, cx - 1, cy - 4, 2, 14, shadow);
  px(ctx, cx - 8, cy + 8, 7, 2, dark);
  px(ctx, cx + 2, cy + 8, 7, 2, shadow);

  // Cinto e fivela: a linha que separa a cintura das pernas, e o lugar da pedra.
  if (it.trim) px(ctx, cx - 8, cy - 5, 17, 1, it.trim);
  gem(ctx, cx, cy - 7, 2, it);
}

/** Botas: par visto de lado, com cano, solado e um pé à frente do outro. */
function drawBoots(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  for (const [index, offset] of [-8, 1].entries()) {
    const body = index === 0 ? base : dark;
    const x = cx + offset;

    px(ctx, x, cy - 9, 7, 13, body);
    px(ctx, x, cy - 9, 2, 13, index === 0 ? light : base);
    // Pé apontando para a direita e solado escuro: sem o pé, o cano lê como caneca.
    px(ctx, x, cy + 3, 10, 5, body);
    px(ctx, x, cy + 7, 10, 2, it.rarity >= ItemRarity.Rare ? STEEL_DARK : '#2e1f13');
    // Cano dobrado.
    px(ctx, x - 1, cy - 10, 9, 3, dark);
    px(ctx, x - 1, cy - 10, 9, 1, light);
    // Fivela do cano, uma por bota: é o detalhe que a peça de faixa alta ganha nos pés, onde
    // não há espaço para pedra sem cobrir o solado.
    if (it.trim) px(ctx, x + 1, cy - 6, 5, 2, it.trim);
  }
}

/**
 * Sapato de pano: sem cano, e com o pé mais longo que a bota.
 *
 * Os dois calçados de conjurador do catálogo saíam como botas de couro, e bota é o oposto
 * do que eles são — a peça leve que se veste **em vez** da armadura. O que os separa a 32 px
 * é a altura: onde a bota tem treze pixels de cano, o sapato tem cinco.
 */
function drawShoes(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  for (const [index, offset] of [-9, 1].entries()) {
    const body = index === 0 ? base : dark;
    const x = cx + offset;

    px(ctx, x, cy - 1, 6, 6, body);
    px(ctx, x, cy - 1, 2, 6, index === 0 ? light : base);
    px(ctx, x, cy + 3, 11, 4, body);
    px(ctx, x, cy + 6, 11, 2, dark);
    // Bico levantado: a curva para cima na ponta é o que faz o pano ler como chinelo de
    // mago em vez de sapato de escritório.
    px(ctx, x + 9, cy + 1, 2, 3, body);
    if (it.trim) px(ctx, x, cy - 2, 6, 1, it.trim);
  }

  gem(ctx, cx - 6, cy + 1, 1.6, it);
}

/**
 * Escudo redondo: aro de metal, e o que ele cerca depende da peça.
 *
 * Tábua no comum e chapa quando a peça sobe — a Égide do crepúsculo com juntas de madeira
 * seria um escudo de aldeia pintado de roxo. O umbo do centro é o mesmo em todos, porque é
 * ele que diz "escudo" antes de qualquer outra coisa; nas peças com pedra, ele é a pedra.
 */
function drawShield(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  it: Finish,
  plated: boolean,
): void {
  const { base, dark, light } = it.ramp;

  ellipse(ctx, cx, cy, 12, 13, plated ? dark : STEEL_DARK);
  ellipse(ctx, cx, cy, 10, 11, plated ? base : WOOD);

  if (plated) {
    // Gomos que saem do umbo: a chapa lisa não tem o que ler, e as nervuras radiais fazem
    // pelo metal o que as juntas fazem pela tábua.
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1;
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2 + Math.PI / 6;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(angle) * 4, cy + Math.sin(angle) * 4);
      ctx.lineTo(cx + Math.cos(angle) * 10, cy + Math.sin(angle) * 11);
      ctx.stroke();
    }
    ellipse(ctx, cx - 3, cy - 5, 5, 3.5, light);
  } else {
    // Tábuas verticais: a junta é o que diz que é madeira montada, não um disco.
    for (const offset of [-6, -2, 2, 6]) {
      px(ctx, cx + offset, cy - 10, 1, 20, WOOD_DARK);
    }
    ellipse(ctx, cx - 3, cy - 4, 5, 4, '#8f6640');
  }

  if (it.trim) {
    ctx.strokeStyle = it.trim;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(cx, cy, 11, 12, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (it.gem) {
    gem(ctx, cx, cy, 4, it);
    return;
  }
  ellipse(ctx, cx, cy, 4, 4, STEEL);
  ellipse(ctx, cx - 1, cy - 1, 2, 2, STEEL_LIGHT);
}

// --- Pano (a linha do conjurador) ----------------------------------------------
//
// Cinco peças que o catálogo veste no lugar da armadura, e que saíam com o corte dela. O
// erro é o mesmo que o cabeçalho deste arquivo descreve para elmo e bota: com uma silhueta
// só, o Manto das constelações e a Brigantina eram o mesmo objeto em duas cores — e quem
// veste manto é justamente quem **não** pode vestir placa.

/** Manto: cai até os pés, abre no peito e tem gola alta. Nenhuma placa faz isso. */
function drawRobe(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  // Barra larga embaixo e ombro estreito em cima: o trapézio é a silhueta do manto, e é o
  // contrário do peitoral, que é largo no ombro e para na cintura.
  ctx.fillStyle = base;
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy - 11);
  ctx.lineTo(cx + 6, cy - 11);
  ctx.lineTo(cx + 12, cy + 13);
  ctx.lineTo(cx - 11, cy + 13);
  ctx.closePath();
  ctx.fill();

  // Dobras: três colunas de sombra que descem da cintura à barra.
  for (const offset of [-6, 0, 6]) {
    px(ctx, cx + offset, cy - 2, 1, 15, dark);
  }
  ctx.fillStyle = light;
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy - 11);
  ctx.lineTo(cx - 1, cy - 11);
  ctx.lineTo(cx - 6, cy + 13);
  ctx.lineTo(cx - 11, cy + 13);
  ctx.closePath();
  ctx.fill();

  // Abertura frontal e gola levantada por trás da nuca.
  px(ctx, cx - 1, cy - 8, 2, 20, dark);
  px(ctx, cx - 6, cy - 14, 13, 4, dark);
  px(ctx, cx - 6, cy - 14, 13, 1, light);
  if (it.trim) {
    px(ctx, cx - 11, cy + 12, 23, 1, it.trim);
    px(ctx, cx - 6, cy - 15, 13, 1, it.trim);
  }

  // Fecho no peito: no manto a pedra fica alta, onde a gola termina.
  gem(ctx, cx, cy - 8, 3, it);
}

/** Capuz: cone caído sobre a nuca, com a sombra do rosto por dentro. */
function drawHood(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  ctx.fillStyle = base;
  ctx.beginPath();
  ctx.moveTo(cx, cy - 13);
  ctx.quadraticCurveTo(cx + 12, cy - 4, cx + 9, cy + 10);
  ctx.lineTo(cx - 9, cy + 10);
  ctx.quadraticCurveTo(cx - 12, cy - 4, cx, cy - 13);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = light;
  ctx.beginPath();
  ctx.moveTo(cx, cy - 13);
  ctx.quadraticCurveTo(cx - 9, cy - 4, cx - 7, cy + 10);
  ctx.lineTo(cx - 2, cy + 10);
  ctx.quadraticCurveTo(cx - 4, cy - 4, cx, cy - 13);
  ctx.closePath();
  ctx.fill();

  // O vazio do rosto: é ele que faz o cone virar capuz em vez de tenda.
  ctx.fillStyle = '#151220';
  ctx.beginPath();
  ctx.ellipse(cx, cy + 3, 5.5, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  px(ctx, cx - 9, cy + 9, 19, 2, dark);
  if (it.trim) px(ctx, cx - 9, cy + 8, 19, 1, it.trim);
  gem(ctx, cx, cy - 8, 2, it);
}

/** Aro fino com uma pedra na testa: tiara e diadema, que não escondem cabeça nenhuma. */
function drawCirclet(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const metal = metalOr(it, GOLD_RAMP);

  ctx.strokeStyle = metal.base;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(cx, cy + 1, 11, 8, 0, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = metal.light;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(cx, cy, 11, 8, 0, Math.PI * 1.05, Math.PI * 1.75);
  ctx.stroke();

  ctx.strokeStyle = metal.dark;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(cx, cy + 2, 11, 8, 0, Math.PI * 0.15, Math.PI * 0.85);
  ctx.stroke();

  // O engaste sobe do aro: sem a haste, a pedra parece pousada no ar acima dele.
  px(ctx, cx - 1, cy - 10, 3, 4, metal.dark);
  if (it.gem) gem(ctx, cx, cy - 11, 3.5, it);
  else ellipse(ctx, cx, cy - 11, 3.5, 3.5, '#5f9be0');
}

/**
 * Coroa: cinco pontas e três pedras.
 *
 * A peça mais rara do slot precisa da silhueta mais **reconhecível** do slot, e coroa é a
 * única forma de cabeça que uma criança desenha sem errar. É por isso que ela não é um elmo
 * dourado: um elmo dourado ainda é um elmo, e a Coroa do dragão ancião tem de ser lida como
 * o fim da linha de dentro de uma mochila cheia.
 */
function drawCrown(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const { base, dark, light } = it.ramp;

  ctx.fillStyle = base;
  ctx.beginPath();
  ctx.moveTo(cx - 12, cy + 4);
  ctx.lineTo(cx - 12, cy - 6);
  ctx.lineTo(cx - 7, cy);
  ctx.lineTo(cx - 4, cy - 10);
  ctx.lineTo(cx, cy - 3);
  ctx.lineTo(cx + 4, cy - 10);
  ctx.lineTo(cx + 7, cy);
  ctx.lineTo(cx + 12, cy - 6);
  ctx.lineTo(cx + 12, cy + 4);
  ctx.closePath();
  ctx.fill();

  // Aro na base, luz à esquerda e sombra à direita — a mesma regra do resto do atlas.
  px(ctx, cx - 12, cy + 4, 25, 5, dark);
  px(ctx, cx - 12, cy + 4, 25, 1, light);
  px(ctx, cx - 12, cy - 6, 2, 10, light);
  px(ctx, cx + 11, cy - 6, 2, 10, dark);

  // Três pedras na base das pontas, e a do meio maior: é a hierarquia que faz o objeto
  // parecer feito por alguém, e não estampado.
  gem(ctx, cx, cy + 6, 2.5, it);
  gem(ctx, cx - 7, cy + 6, 1.6, it);
  gem(ctx, cx + 7, cy + 6, 1.6, it);
  if (it.trim) {
    px(ctx, cx - 4, cy - 12, 2, 2, it.trim);
    px(ctx, cx + 3, cy - 12, 2, 2, it.trim);
  }
}

// --- Diversos ----------------------------------------------------------------

/**
 * A rampa de metal de uma moeda, escolhida pela **posição na escada** e não pelo id.
 *
 * Pela posição porque é o que sobrevive a um degrau novo: uma moeda entre a platina e o
 * cristal herdaria a cor do meio sem ninguém tocar aqui, e um `switch` por id a desenharia
 * dourada até alguém notar. O de baixo é sempre o ouro e o de cima é sempre o cristal, que é
 * a convenção do gênero e, mais importante, a única leitura que funciona de relance: o que
 * não é dourado na mochila vale muito mais do que parece.
 */
function coinRamp(type: ItemType): readonly [dark: string, mid: string, light: string] {
  if (type.value <= (COINS.at(-1)?.value ?? 1)) return [GOLD_DARK, GOLD, GOLD_LIGHT];
  if (type.value >= (COINS[0]?.value ?? 1)) return [CRYSTAL_DARK, CRYSTAL, CRYSTAL_LIGHT];
  return [PLATINUM_DARK, PLATINUM, PLATINUM_LIGHT];
}

/** Moedas empilhadas: o único item que o jogador conta de longe. */
function drawCoins(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  [dark, mid, light]: readonly [string, string, string],
): void {
  ellipse(ctx, cx, cy + 5, 9, 4, dark);
  ellipse(ctx, cx, cy + 2, 9, 4, mid);
  ellipse(ctx, cx, cy - 1, 9, 4, mid);
  ellipse(ctx, cx, cy - 4, 9, 4, light);
  ellipse(ctx, cx, cy - 4, 5, 2, mid);
  px(ctx, cx - 5, cy - 6, 3, 2, '#fff3b0');
}

/** Mochila: corpo, aba, fivela e alças. O tamanho da aba cresce com a capacidade. */
function drawBackpack(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  capacity: number,
  it: Finish,
): void {
  const width = capacity > 10 ? 19 : 15;
  const half = Math.floor(width / 2);
  // Contêiner é couro em qualquer faixa: a Mochila reforçada é rara e continua sendo uma
  // mochila. O que a raridade muda aqui é a ferragem, não a matéria — costurar aço numa
  // bolsa a transformaria num baú.
  const body = metalOr(it, LEATHER_RAMP);

  px(ctx, cx - half, cy - 5, width, 16, body.base);
  px(ctx, cx - half, cy - 5, 4, 16, body.light);
  px(ctx, cx + half - 4, cy - 5, 4, 16, body.dark);

  // Aba e fivela.
  px(ctx, cx - half, cy - 8, width, 6, body.dark);
  px(ctx, cx - half, cy - 8, width, 1, body.light);
  px(ctx, cx - 2, cy - 3, 5, 4, it.trim ?? GOLD);
  px(ctx, cx - 1, cy - 2, 3, 2, GOLD_DARK);

  // Alças, saindo por trás do corpo.
  px(ctx, cx - half - 2, cy - 4, 2, 11, body.dark);
  px(ctx, cx + half, cy - 4, 2, 11, body.dark);
  gem(ctx, cx, cy - 6, 2, it);
}

/**
 * Cor da inscrição de cada runa.
 *
 * A pedra é sempre a mesma, e só a inscrição muda de cor: é o que faz seis itens diferentes
 * lerem como uma **família** no inventário, do mesmo jeito que couro e aço agrupam as peças
 * de equipamento. A cor segue o efeito — fogo, cura, energia —, que é o vocabulário que o
 * jogador já aprendeu vendo magia na tela.
 *
 * Ela vem do efeito da **magia** que a runa conjura, e não de uma lista de ids: `spellId` já
 * está no catálogo porque o servidor precisa dele para lançar, e a lista paralela envelheceu
 * exatamente como se esperava — as três runas de §15.11 nasceram todas laranja, e a Runa de
 * gelo saía da cor do fogo no lugar em que a cor é a única coisa que a distingue.
 */
function runeGlow(type: ItemType): string {
  const effect = type.spellId === undefined ? undefined : SPELLS[type.spellId]?.effect;

  switch (effect) {
    case EffectId.Heal:
      return '#5fd48b';
    case EffectId.Energy:
      return '#8f7fe8';
    case EffectId.Ice:
      return '#6fc4ee';
    case EffectId.Poison:
      return '#8fbe45';
    default:
      return '#e07a3c';
  }
}

/** Pedra chata com uma inscrição gravada. Acesa quando a runa tem carga. */
function drawRune(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  glow: string | null,
): void {
  // Contorno de pedra lascada: um octógono irregular lê como pedra, um círculo lê como moeda.
  ctx.fillStyle = '#6e6a63';
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy - 11);
  ctx.lineTo(cx + 5, cy - 10);
  ctx.lineTo(cx + 10, cy - 2);
  ctx.lineTo(cx + 8, cy + 8);
  ctx.lineTo(cx - 2, cy + 11);
  ctx.lineTo(cx - 9, cy + 6);
  ctx.lineTo(cx - 10, cy - 4);
  ctx.closePath();
  ctx.fill();

  // Luz do noroeste, como no resto do catálogo.
  ctx.fillStyle = '#8d8880';
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy - 11);
  ctx.lineTo(cx + 5, cy - 10);
  ctx.lineTo(cx - 1, cy - 3);
  ctx.lineTo(cx - 10, cy - 4);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx - 2, cy + 7, 10, 3, '#4c4944');

  if (!glow) {
    // Sulco vazio: a inscrição existe, só não está acesa.
    px(ctx, cx - 1, cy - 6, 2, 11, '#4c4944');
    px(ctx, cx - 5, cy - 2, 10, 2, '#4c4944');
    return;
  }

  // Inscrição: haste vertical, travessão e dois serifos. Fina de propósito — a leitura
  // é a cor, e um traço grosso viraria mancha em 32 px.
  px(ctx, cx - 1, cy - 7, 2, 13, glow);
  px(ctx, cx - 5, cy - 3, 10, 2, glow);
  px(ctx, cx - 5, cy - 7, 2, 3, glow);
  px(ctx, cx + 3, cy + 2, 2, 3, glow);

  // Halo: um pixel translúcido em volta da haste, que é o que dá a impressão de brilho.
  px(ctx, cx - 2, cy - 8, 4, 1, `${glow}88`);
  px(ctx, cx - 2, cy + 6, 4, 1, `${glow}88`);
}

/**
 * As duas caras do mesmo corpo (PRD §15.7).
 *
 * O corpo apodrecido é o aviso de que o que está no chão vai sumir, e ele só funciona se o
 * jogador ler **o mesmo objeto em outro estado** — não um objeto diferente. Por isso o que muda
 * entre os dois é a paleta, e nem um pixel do traço: um segundo desenho, com outra pose ou
 * outra silhueta, seria lido como outro item largado por cima do corpo, e quem voltasse correndo
 * ao lugar da morte passaria direto procurando o vulto que conhece.
 *
 * A escolha das cores tem uma regra só: o podre perde o vermelho vivo. A poça fresca é o único
 * vermelho grande do catálogo — é ela que grita "morreu aqui agora"; a mancha esverdeada que
 * fica no lugar diz que o aqui-agora já passou.
 */
interface CorpsePalette {
  readonly poolDeep: string;
  readonly pool: string;
  readonly cloth: string;
  readonly clothLight: string;
  readonly clothDark: string;
  readonly skin: string;
  readonly skinLight: string;
  readonly boot: string;
}

const CORPSE_FRESH: CorpsePalette = {
  poolDeep: '#5e1418',
  pool: '#8c1f24',
  cloth: LEATHER,
  clothLight: LEATHER_LIGHT,
  clothDark: LEATHER_DARK,
  skin: '#c9a07e',
  skinLight: '#e0bd9c',
  boot: '#3a2616',
};

const CORPSE_ROTTEN: CorpsePalette = {
  poolDeep: '#232c1c',
  pool: '#3f4d28',
  cloth: '#6b5c42',
  clothLight: '#89795a',
  clothDark: '#463c2b',
  skin: '#93a37a',
  skinLight: '#b2bf95',
  boot: '#2a2419',
};

/**
 * Corpo: um vulto caído, visto de cima, sobre uma poça.
 *
 * Precisa ser reconhecível **antes** de o jogador ler nome nenhum, porque o momento em que
 * ele importa é o da volta correndo ao lugar onde se morreu — e quem está correndo lê
 * silhueta, não rótulo. Duas coisas fazem isso: a poça, que é o único vermelho grande do
 * catálogo, e o eixo deitado, que é a única forma horizontal entre ícones todos verticais.
 */
function drawCorpse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: CorpsePalette,
): void {
  // Poça: mancha irregular por baixo de tudo, para o vulto parecer estar sobre ela.
  ellipse(ctx, cx, cy + 4, 13, 8, palette.poolDeep);
  ellipse(ctx, cx - 2, cy + 3, 10, 6, palette.pool);
  ellipse(ctx, cx + 5, cy + 7, 4, 2.5, palette.pool);

  // Tronco deitado, da esquerda para a direita, com a sombra do lado de baixo.
  px(ctx, cx - 8, cy - 3, 15, 7, palette.cloth);
  px(ctx, cx - 8, cy - 3, 15, 2, palette.clothLight);
  px(ctx, cx - 8, cy + 2, 15, 2, palette.clothDark);

  // Cabeça caída para o lado, e um braço estendido: são os dois traços que separam
  // "corpo" de "saco".
  ellipse(ctx, cx - 10, cy - 4, 4.5, 4.5, palette.skin);
  ellipse(ctx, cx - 11, cy - 5, 2, 2, palette.skinLight);
  px(ctx, cx + 6, cy - 6, 6, 3, palette.skin);
  px(ctx, cx + 10, cy - 7, 3, 3, palette.skin);

  // Perna dobrada.
  px(ctx, cx + 6, cy + 2, 5, 3, palette.clothDark);
  px(ctx, cx + 9, cy + 4, 3, 4, palette.boot);
}

/**
 * Frasco: vidro claro em cima, líquido colorido embaixo, rolha de cortiça.
 *
 * O **tamanho vem da cura**, e é a metade que faltava. Oito poções dividem esta função, e
 * quatro delas são vermelhas: com um frasco só, a Poção de vida pequena e a suprema eram o
 * mesmo ícone, e o jogador que abre a mochila com 20 de vida escolhe pela posição no
 * inventário. Errar aqui é irreversível — a poção some ao ser bebida —, e é justamente o
 * tipo de erro que acontece na hora em que não há tempo de ler nome nenhum.
 */
function drawPotion(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  type: ItemType,
  it: Finish,
): void {
  const liquid = type.healsMana && !type.healsHealth ? '#4f7fd0' : '#d04f5f';
  const restores = Math.max(type.healsHealth ?? 0, type.healsMana ?? 0);
  // Três portes, e o corte é onde o catálogo já separa as famílias: pequena até 40, comum e
  // forte até 180, suprema acima disso.
  const size = restores > 180 ? 2 : restores > 40 ? 1 : 0;

  const half = 4 + size;
  const height = 13 + size * 3;
  const top = cy - 7 - size * 2;

  px(ctx, cx - 3, top - 6, 6, 3, '#9a7a4a');
  px(ctx, cx - 2, top - 3, 5, 3, '#cfd6de');
  px(ctx, cx - half, top, half * 2, height, '#e8eef5');
  px(ctx, cx - half + 1, top + 5, half * 2 - 2, height - 6, liquid);
  // Menisco claro e reflexo vertical: dois traços que transformam a caixa em vidro.
  px(ctx, cx - half + 1, top + 5, half * 2 - 2, 1, '#ffffff88');
  px(ctx, cx - half + 2, top + 7, 2, height - 10, '#ffffff55');
  px(ctx, cx - half, top + height - 2, half * 2, 2, '#b9c2cc');

  // Lacre no gargalo: as poções fortes e supremas são o mesmo frasco com o mesmo líquido, e
  // sem uma marca o jogador bebe a suprema onde a pequena bastava.
  if (it.trim) {
    px(ctx, cx - 4, top - 4, 9, 2, it.trim);
    px(ctx, cx - half, top + 1, half * 2, 1, it.trim);
  }
  // A poção que cura os dois recursos leva a segunda cor numa faixa, e não misturada: água
  // benta e elixir são as únicas duas do catálogo assim, e um roxo médio não diria nem uma
  // coisa nem outra.
  if (type.healsHealth && type.healsMana) px(ctx, cx - half + 1, top + 6, half * 2 - 2, 3, '#4f7fd0');
}

/** Pele de lobo: retângulo de couro com as patas abertas. */
function drawPelt(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  px(ctx, cx - 6, cy - 9, 13, 19, '#7b8089');
  px(ctx, cx - 6, cy - 9, 5, 19, '#9aa0aa');
  px(ctx, cx - 10, cy - 7, 5, 6, '#6a7079');
  px(ctx, cx + 6, cy - 7, 5, 6, '#6a7079');
  px(ctx, cx - 10, cy + 3, 5, 6, '#6a7079');
  px(ctx, cx + 6, cy + 3, 5, 6, '#6a7079');
  px(ctx, cx - 3, cy - 4, 7, 9, '#cfd4dc');
}

/** Presa de troll: cone claro, curvo, com a base escura. */
function drawTusk(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = '#efe7d2';
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy + 11);
  ctx.quadraticCurveTo(cx + 9, cy + 4, cx + 4, cy - 12);
  ctx.quadraticCurveTo(cx + 1, cy + 2, cx - 9, cy + 9);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#c9bfa4';
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy + 11);
  ctx.quadraticCurveTo(cx + 4, cy + 5, cx + 4, cy - 12);
  ctx.quadraticCurveTo(cx + 3, cy + 4, cx - 7, cy + 10);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx - 8, cy + 8, 6, 4, '#8f8570');
}

/** Rabo de rato: um cordão curvo com anelação. */
function drawTail(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.strokeStyle = '#c49a8a';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy + 10);
  ctx.quadraticCurveTo(cx + 12, cy + 6, cx + 2, cy - 4);
  ctx.quadraticCurveTo(cx - 5, cy - 10, cx + 4, cy - 12);
  ctx.stroke();

  ctx.strokeStyle = '#8f6a5c';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy + 11);
  ctx.quadraticCurveTo(cx + 12, cy + 7, cx + 2, cy - 3);
  ctx.stroke();
}

// --- Comida ------------------------------------------------------------------
//
// Seis itens que fazem a mesma coisa e só diferem em quanto alimentam (PRD §15.7), então a
// única coisa que os separa na tela é a **forma**. A regra de desenho aqui não é realismo: é
// que nenhuma das seis silhuetas possa ser confundida com outra a 32×32, que é o tamanho em
// que elas de fato aparecem (§8.1). Daí uma família de eixos e contornos diferentes de
// propósito — cúpula, oval, triângulo, lasca deitada, perfil com cauda e gota alta —, e não
// seis variações do mesmo bloco pintadas de cores diferentes.
//
// A ordem aqui é a da tabela de §15.7, do que menos alimenta ao que mais, e não a dos ids:
// quem abre este arquivo está comparando as seis silhuetas entre si, e a tabela é essa mesma
// comparação em números. Ordenar por id enfileiraria o Peixe, que é o 96 por herança, longe
// dos cinco que ele precisa não parecer.

/** Cogumelo: cúpula sobre talo fino — o único guarda-chuva daqui, lido a 32×32 (PRD §8.1). */
function drawMushroom(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Meia elipse, e não a elipse inteira: o chapéu precisa de base reta para a lamela caber
  // debaixo dele. Com a elipse cheia, a barriga de baixo engole a lamela e o que fica na tela
  // é uma bola espetada num pau — que é exatamente o bastão do catálogo com a gema no topo.
  ctx.fillStyle = MUSHROOM;
  ctx.beginPath();
  ctx.ellipse(cx, cy - 1, 12, 9, 0, Math.PI, Math.PI * 2);
  ctx.closePath();
  ctx.fill();

  ellipse(ctx, cx - 4, cy - 6, 5, 3, MUSHROOM_LIGHT);

  // Pintas claras: é o que impede a cúpula de ler como capuz ou como escudo de perfil.
  ellipse(ctx, cx + 4, cy - 5, 2.5, 2, MUSHROOM_STEM);
  ellipse(ctx, cx - 7, cy - 3, 2, 1.5, MUSHROOM_STEM);
  ellipse(ctx, cx, cy - 8, 2, 1.5, MUSHROOM_STEM);

  // Lamela: a faixa escura sob o chapéu, mais estreita que ele, para a borda ficar em balanço.
  px(ctx, cx - 10, cy - 1, 21, 3, MUSHROOM_DARK);

  // Talo por último, passando na frente da lamela — é a ordem que emenda as duas peças.
  px(ctx, cx - 3, cy, 6, 12, MUSHROOM_STEM);
  px(ctx, cx + 1, cy, 2, 12, MUSHROOM_STEM_DARK);
  px(ctx, cx - 5, cy + 9, 10, 3, MUSHROOM_STEM);
  px(ctx, cx - 5, cy + 11, 10, 1, MUSHROOM_STEM_DARK);
}

/** Pão: oval gordo com talhos na crosta — a única forma sem quina nem haste (PRD §8.1). */
function drawBread(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ellipse(ctx, cx, cy + 1, 13, 9, BREAD_DARK);
  ellipse(ctx, cx, cy, 12.5, 8, BREAD);
  ellipse(ctx, cx - 3, cy - 3, 7, 3.5, BREAD_LIGHT);

  // Três talhos em escada: o oval sozinho lê como pedra, e é o corte na crosta que diz que
  // aquilo cresceu no forno. Em escada porque diagonal fina em 32 px vira serrilha cinza.
  for (const offset of [-7, -1, 5]) {
    for (let step = 0; step < 4; step++) {
      px(ctx, cx + offset + step, cy - 5 + step * 2, 2, 2, BREAD_CRUMB);
    }
  }
}

/** Queijo: cunha com furos — o único triângulo do catálogo, lido antes mesmo da cor (PRD §8.1). */
function drawCheese(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Face cortada, de frente.
  ctx.fillStyle = CHEESE;
  ctx.beginPath();
  ctx.moveTo(cx - 13, cy + 9);
  ctx.lineTo(cx + 12, cy + 9);
  ctx.lineTo(cx + 12, cy - 3);
  ctx.closePath();
  ctx.fill();

  // Topo mais claro: é a espessura da cunha. Sem ele o desenho é um triângulo chapado, e um
  // triângulo chapado a 32 px lê como bandeirola.
  ctx.fillStyle = CHEESE_LIGHT;
  ctx.beginPath();
  ctx.moveTo(cx - 13, cy + 9);
  ctx.lineTo(cx + 12, cy - 3);
  ctx.lineTo(cx + 12, cy - 8);
  ctx.lineTo(cx - 13, cy + 4);
  ctx.closePath();
  ctx.fill();

  // Ponta grossa em sombra, do lado oposto à luz do noroeste.
  px(ctx, cx + 9, cy, 4, 9, CHEESE_DARK);

  // Olhaduras: o traço que separa o queijo de qualquer outra cunha amarela.
  ellipse(ctx, cx + 2, cy + 5, 2.5, 2.5, CHEESE_DARK);
  ellipse(ctx, cx + 7, cy + 3, 2, 2, CHEESE_DARK);
  ellipse(ctx, cx - 3, cy + 7, 1.6, 1.6, CHEESE_DARK);
}

/** Carne: lasca deitada, sem osso — larga e escura onde o presunto é alto e rosado (PRD §8.1). */
function drawMeat(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Contorno irregular de propósito: carne cortada não tem aresta reta, e o polígono torto é
  // o que impede esta peça de virar o mesmo retângulo vermelho que uma poção de vida deitada.
  ctx.fillStyle = MEAT_DARK;
  ctx.beginPath();
  ctx.moveTo(cx - 11, cy - 3);
  ctx.lineTo(cx - 4, cy - 10);
  ctx.lineTo(cx + 7, cy - 9);
  ctx.lineTo(cx + 12, cy - 1);
  ctx.lineTo(cx + 8, cy + 8);
  ctx.lineTo(cx - 5, cy + 10);
  ctx.lineTo(cx - 12, cy + 3);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = MEAT;
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy - 3);
  ctx.lineTo(cx - 3, cy - 8);
  ctx.lineTo(cx + 6, cy - 7);
  ctx.lineTo(cx + 10, cy - 1);
  ctx.lineTo(cx + 6, cy + 6);
  ctx.lineTo(cx - 5, cy + 8);
  ctx.lineTo(cx - 10, cy + 2);
  ctx.closePath();
  ctx.fill();

  // Marmoreio.
  px(ctx, cx - 5, cy - 3, 9, 2, MEAT_LIGHT);
  px(ctx, cx - 2, cy + 1, 7, 2, MEAT_LIGHT);
  px(ctx, cx - 6, cy + 4, 5, 1, MEAT_LIGHT);

  // Capa de gordura numa borda só: é o claro que quebra a mancha vermelha e a faz ler como
  // corte, e não como poça — que é o que a poça do corpo já ocupa no vocabulário do jogador.
  ctx.fillStyle = FAT;
  ctx.beginPath();
  ctx.moveTo(cx + 12, cy - 1);
  ctx.lineTo(cx + 8, cy + 8);
  ctx.lineTo(cx + 4, cy + 8);
  ctx.lineTo(cx + 8, cy - 2);
  ctx.closePath();
  ctx.fill();
}

/** Peixe: corpo fusiforme com cauda bifurcada — cauda nenhum outro ícone daqui tem (PRD §8.1). */
function drawFish(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Cauda antes do corpo, para a emenda ficar escondida sob a elipse.
  ctx.fillStyle = FISH_DARK;
  ctx.beginPath();
  ctx.moveTo(cx + 6, cy);
  ctx.lineTo(cx + 14, cy - 7);
  ctx.lineTo(cx + 12, cy);
  ctx.lineTo(cx + 14, cy + 7);
  ctx.closePath();
  ctx.fill();

  // Barbatana dorsal: sem ela a elipse com cauda lê como folha, que é o que este ícone mais
  // arrisca — e folha e peixe caem no mesmo lugar da mochila, o das coisas apanhadas do chão.
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy - 5);
  ctx.lineTo(cx - 1, cy - 11);
  ctx.lineTo(cx + 3, cy - 4);
  ctx.closePath();
  ctx.fill();

  ellipse(ctx, cx - 2, cy, 11, 6.5, FISH);
  ellipse(ctx, cx - 2, cy - 2, 10.5, 4, FISH_DARK);
  ellipse(ctx, cx - 2, cy + 3, 9, 3, FISH_LIGHT);

  // Barbatana ventral, opérculo e olho: três marcas pequenas que dão o sentido da cabeça, e
  // um peixe sem sentido de cabeça a 32 px é um losango.
  ctx.fillStyle = FISH_DARK;
  ctx.beginPath();
  ctx.moveTo(cx - 1, cy + 4);
  ctx.lineTo(cx + 2, cy + 9);
  ctx.lineTo(cx + 4, cy + 3);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx - 6, cy - 3, 1, 7, FISH_DARK);
  ellipse(ctx, cx - 8, cy - 1, 2.2, 2.2, '#f6f2e8');
  ellipse(ctx, cx - 8, cy - 1, 1.1, 1.1, OUTLINE);
}

/** Presunto: pernil em gota com osso na ponta — alto e rosado onde a carne é baixa (PRD §8.1). */
function drawHam(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Pescoço primeiro, e a coxa por cima: a elipse cobre onde os dois se encontram, e o pernil
  // sai como uma peça só em vez de um triângulo apoiado num ovo.
  ctx.fillStyle = HAM_DARK;
  ctx.beginPath();
  ctx.moveTo(cx - 7, cy + 3);
  ctx.quadraticCurveTo(cx - 4, cy - 5, cx - 2, cy - 7);
  ctx.lineTo(cx + 5, cy - 6);
  ctx.quadraticCurveTo(cx + 6, cy - 1, cx + 8, cy + 4);
  ctx.closePath();
  ctx.fill();

  ellipse(ctx, cx, cy + 4, 11, 8.5, HAM_DARK);
  ellipse(ctx, cx - 1, cy + 3, 10, 7.5, HAM);
  ellipse(ctx, cx - 4, cy, 4.5, 3.5, HAM_LIGHT);

  // Escoriações da casca: três riscos curtos, para a gota não ler como balão.
  for (const [x, y] of [
    [1, 4],
    [4, 1],
    [-1, 8],
  ] as const) {
    px(ctx, cx + x, cy + y, 5, 1, HAM_DARK);
  }

  // Osso saindo pela ponta estreita — é ele que separa o presunto da carne, que é a outra peça
  // de açougue do catálogo, e o que dá ao ícone o eixo vertical que a carne não tem.
  px(ctx, cx - 1, cy - 11, 5, 7, BONE);
  px(ctx, cx + 2, cy - 11, 2, 7, BONE_DARK);
  ellipse(ctx, cx + 1, cy - 11, 3.5, 3, BONE);
  ellipse(ctx, cx, cy - 12, 1.5, 1.2, '#fbf6e8');
}

// --- Ferramentas (PRD §15.4) ---------------------------------------------------
//
// Cinco objetos e uma máquina só — mas cinco silhuetas. A ferramenta é o item que se procura
// na mochila na frente do obstáculo, e nenhuma das cinco pode ser confundida com outra nem
// com uma arma: o machado de lenhador tem cabo de trabalho e o fio virado para madeira.

/** Pá: punho em T, cabo e a lâmina de aço afunilando — a lâmina embaixo, como quem cava. */
function drawShovel(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  px(ctx, cx - 4, cy - 13, 9, 3, WOOD_DARK);
  px(ctx, cx - 1, cy - 11, 3, 14, WOOD);
  px(ctx, cx - 1, cy - 11, 1, 14, '#8f6640');

  ctx.fillStyle = STEEL;
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy + 3);
  ctx.lineTo(cx + 6, cy + 3);
  ctx.lineTo(cx + 6, cy + 9);
  ctx.lineTo(cx + 2, cy + 13);
  ctx.lineTo(cx - 1, cy + 13);
  ctx.lineTo(cx - 5, cy + 9);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx - 5, cy + 3, 3, 7, STEEL_LIGHT);
  px(ctx, cx + 3, cy + 4, 3, 6, STEEL_DARK);
}

/** Picareta: o arco de duas pontas atravessado no cabo — a ferramenta que não volta atrás. */
function drawPickaxe(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  px(ctx, cx - 1, cy - 8, 3, 21, WOOD);
  px(ctx, cx - 1, cy - 8, 1, 21, '#8f6640');
  px(ctx, cx - 2, cy + 11, 5, 2, WOOD_DARK);

  ctx.strokeStyle = STEEL;
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy + 2, 12, Math.PI * 1.15, Math.PI * 1.85);
  ctx.stroke();
  ctx.strokeStyle = STEEL_LIGHT;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy + 1, 12, Math.PI * 1.2, Math.PI * 1.8);
  ctx.stroke();
}

/**
 * Machado de lenhador: cabo longo de trabalho e uma cabeça só, funda.
 *
 * Precisa se separar de duas coisas ao mesmo tempo: do Machado de mão (21), que é arma e
 * tem cabo curto, e do Machado de guerra (102), que tem duas meias-luas. O cabo comprido
 * com o enrolamento de couro é o que diz "isto se segura com as duas mãos para trabalhar".
 */
function drawLumberAxe(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  px(ctx, cx - 2, cy - 13, 3, 27, WOOD);
  px(ctx, cx - 2, cy - 13, 1, 27, '#8f6640');
  px(ctx, cx - 3, cy + 12, 5, 2, WOOD_DARK);
  for (const y of [4, 7]) {
    px(ctx, cx - 2, cy + y, 3, 1, LEATHER_DARK);
  }

  ctx.fillStyle = STEEL;
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 12);
  ctx.quadraticCurveTo(cx + 14, cy - 11, cx + 13, cy - 1);
  ctx.quadraticCurveTo(cx + 8, cy + 3, cx + 1, cy + 2);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = STEEL_LIGHT;
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 12);
  ctx.quadraticCurveTo(cx + 11, cy - 11, cx + 11, cy - 5);
  ctx.lineTo(cx + 1, cy - 5);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx - 4, cy - 12, 8, 3, STEEL_DARK);
}

/** Faca: a menor lâmina do catálogo, diagonal, com o cabo de couro. */
function drawKnife(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = STEEL;
  ctx.beginPath();
  ctx.moveTo(cx - 2, cy + 2);
  ctx.lineTo(cx + 9, cy - 9);
  ctx.lineTo(cx + 11, cy - 5);
  ctx.lineTo(cx + 1, cy + 4);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = STEEL_LIGHT;
  ctx.beginPath();
  ctx.moveTo(cx - 2, cy + 2);
  ctx.lineTo(cx + 9, cy - 9);
  ctx.lineTo(cx + 10, cy - 7);
  ctx.lineTo(cx - 1, cy + 3);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx - 3, cy + 1, 3, 3, STEEL_DARK);
  ctx.fillStyle = LEATHER;
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy + 4);
  ctx.lineTo(cx - 10, cy + 10);
  ctx.lineTo(cx - 8, cy + 12);
  ctx.lineTo(cx - 2, cy + 6);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx - 9, cy + 9, 2, 2, LEATHER_DARK);
  px(ctx, cx - 6, cy + 6, 2, 2, LEATHER_DARK);
}

/** Vara de pescar: vara diagonal, a linha caindo da ponta e o anzol — a linha é a assinatura. */
function drawFishingRod(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.strokeStyle = WOOD;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy + 13);
  ctx.quadraticCurveTo(cx + 2, cy + 2, cx + 10, cy - 12);
  ctx.stroke();
  ctx.strokeStyle = '#8f6640';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy + 12);
  ctx.quadraticCurveTo(cx + 2, cy + 2, cx + 9, cy - 11);
  ctx.stroke();
  px(ctx, cx - 10, cy + 9, 4, 3, LEATHER_DARK);

  // Linha reta da ponta à água que não existe, e o anzol de meia-volta na ponta.
  px(ctx, cx + 10, cy - 12, 1, 20, '#e8e2cf');
  ctx.strokeStyle = STEEL_LIGHT;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx + 8, cy + 8, 3, Math.PI * 1.9, Math.PI * 0.9);
  ctx.stroke();
}

/** Chave de bronze: argola, haste e o palhetão de dentes desiguais — iguais, leria pé de mesa. */
function drawKey(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.strokeStyle = BRONZE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy - 8, 4.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = BRONZE_LIGHT;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx - 1, cy - 9, 4.5, Math.PI * 0.9, Math.PI * 1.7);
  ctx.stroke();

  px(ctx, cx - 1, cy - 3, 3, 14, BRONZE);
  px(ctx, cx - 1, cy - 3, 1, 14, BRONZE_LIGHT);
  px(ctx, cx + 2, cy + 7, 4, 2, BRONZE);
  px(ctx, cx + 2, cy + 10, 3, 2, BRONZE_DARK);
}

// --- Equipamento de faixa alta (PRD §15.5) --------------------------------------

/** Machado de guerra: duas meias-luas espelhadas — o dobro do machado comum, e se lê assim. */
function drawWarAxe(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const head = metalOr(it, STEEL_RAMP);

  px(ctx, cx - 1, cy - 13, 3, 27, WOOD);
  px(ctx, cx - 1, cy - 13, 1, 27, '#8f6640');
  px(ctx, cx - 2, cy + 12, 5, 2, WOOD_DARK);
  px(ctx, cx - 2, cy - 14, 5, 3, head.dark);

  for (const side of [-1, 1] as const) {
    ctx.fillStyle = head.base;
    ctx.beginPath();
    ctx.moveTo(cx + side * 2, cy - 11);
    ctx.quadraticCurveTo(cx + side * 13, cy - 9, cx + side * 12, cy + 1);
    ctx.lineTo(cx + side * 2, cy - 1);
    ctx.closePath();
    ctx.fill();

    // A face da esquerda pega a luz do noroeste; a da direita fica em sombra.
    ctx.fillStyle = side < 0 ? head.light : head.dark;
    ctx.beginPath();
    ctx.moveTo(cx + side * 2, cy - 11);
    ctx.quadraticCurveTo(cx + side * 10, cy - 9, cx + side * 10, cy - 4);
    ctx.lineTo(cx + side * 2, cy - 6);
    ctx.closePath();
    ctx.fill();
  }

  // Fita de ouro no pescoço da cabeça: é o que diz "guerra", e não "lenha".
  px(ctx, cx - 2, cy + 1, 5, 2, it.trim ?? GOLD);
  gem(ctx, cx, cy + 5, 2, it);
}

/** Placa de escamas: o corte de peitoral das outras, coberto por fiadas sobrepostas. */
function drawScaleArmor(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  px(ctx, cx - 7, cy - 8, 15, 17, SCALE);
  px(ctx, cx - 11, cy - 7, 5, 7, SCALE_DARK);
  px(ctx, cx + 7, cy - 7, 5, 7, SCALE_DARK);
  px(ctx, cx - 11, cy - 7, 5, 2, SCALE_LIGHT);
  px(ctx, cx - 4, cy - 10, 9, 3, SCALE_DARK);

  // Fiadas de escama: meias-luas desencontradas linha a linha — é a sobreposição que diz
  // "escama", não a cor.
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 5; col++) {
      const x = cx - 6 + col * 3 + (row % 2 === 0 ? 0 : 1);
      const y = cy - 7 + row * 3;
      if (x > cx + 6) continue;
      ctx.fillStyle = row % 2 === 0 ? SCALE_LIGHT : SCALE;
      ctx.beginPath();
      ctx.arc(x + 1, y + 1, 1.6, 0, Math.PI);
      ctx.fill();
    }
  }

  px(ctx, cx - 7, cy + 6, 15, 3, SCALE_DARK);
  px(ctx, cx - 1, cy + 6, 3, 3, STEEL);
}

/** Cajado rúnico: a gema em garra dupla e as marcas acesas na haste. */
function drawRunicStaff(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  px(ctx, cx - 1, cy - 7, 3, 21, WOOD);
  px(ctx, cx - 1, cy - 7, 1, 21, '#8f6640');
  px(ctx, cx - 2, cy + 12, 5, 2, WOOD_DARK);

  // Runas na cor da gema: a madeira lembra o que conjurou — e é o que o separa do Bastão
  // (24), que é a mesma silhueta sem passado nenhum.
  const glow = it.gem ?? '#8f7fe8';
  px(ctx, cx - 1, cy - 1, 3, 1, glow);
  px(ctx, cx, cy + 3, 2, 1, glow);
  px(ctx, cx - 1, cy + 7, 3, 1, glow);

  const claw = metalOr(it, STEEL_RAMP);
  px(ctx, cx - 4, cy - 8, 9, 3, claw.dark);
  px(ctx, cx - 4, cy - 8, 9, 1, it.trim ?? claw.base);
  px(ctx, cx - 4, cy - 11, 2, 4, claw.dark);
  px(ctx, cx + 3, cy - 11, 2, 4, claw.dark);

  ellipse(ctx, cx, cy - 11, 4, 4, it.gem ? claw.dark : '#5b4a9e');
  ellipse(ctx, cx, cy - 11, 2.8, 2.8, glow);
  ellipse(ctx, cx - 1, cy - 12, 1.3, 1.3, it.gem ? it.gemLight : '#e8e2ff');
}

/** Amuleto: a corrente aberta e o pingente com a pedra — o que se pendura no peito. */
function drawAmulet(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const metal = metalOr(it, GOLD_RAMP);

  // Elos como pontos ao longo do colar: desenhar a corrente cheia viraria um risco preto.
  const links = [
    [-8, -10],
    [-9, -6],
    [-8, -2],
    [-6, 1],
    [8, -10],
    [9, -6],
    [8, -2],
    [6, 1],
    [-4, -12],
    [4, -12],
  ] as const;
  for (const [dx, dy] of links) {
    px(ctx, cx + dx, cy + dy, 2, 2, metal.dark);
  }
  px(ctx, cx - 1, cy - 13, 2, 2, metal.base);

  px(ctx, cx - 3, cy + 2, 7, 3, metal.base);
  ellipse(ctx, cx, cy + 7, 4.5, 5, metal.base);
  // O vermelho é do Amuleto de vida (110), que é o que o nome dele promete. As peças de
  // faixa alta trazem a pedra da própria raridade — ver `rarity.ts`.
  ellipse(ctx, cx, cy + 7, 3, 3.5, it.gem ?? '#c03a44');
  ellipse(ctx, cx - 1, cy + 6, 1.2, 1.5, it.gem ? it.gemLight : '#ff9aa0');
}

/**
 * Anel de força: um aro que preenche o quadro.
 *
 * No tamanho real ninguém o acharia na mochila — o ícone é o item **explicado**, não em
 * escala, que é a mesma licença das moedas empilhadas.
 */
function drawRing(ctx: CanvasRenderingContext2D, cx: number, cy: number, it: Finish): void {
  const metal = metalOr(it, GOLD_RAMP);

  ctx.strokeStyle = metal.base;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(cx, cy + 2, 8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = metal.dark;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy + 2, 6.2, Math.PI * 0.1, Math.PI * 0.9);
  ctx.stroke();
  ctx.strokeStyle = metal.light;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy + 2, 9.4, Math.PI * 1.1, Math.PI * 1.6);
  ctx.stroke();

  // Engaste e gema no alto do aro.
  px(ctx, cx - 4, cy - 9, 9, 4, metal.dark);
  ellipse(ctx, cx, cy - 8, 3.2, 3, it.gem ?? '#c8503c');
  ellipse(ctx, cx - 1, cy - 9, 1.2, 1.1, it.gem ? it.gemLight : '#ffb09a');
}

// --- O que saiu da bolha genérica (PRD §15.11) ----------------------------------
//
// Quatro alimentos e treze matérias-primas. A regra de desenho é a mesma que §15.7 usou para
// a comida — nenhuma silhueta pode ser confundida com outra a 32 px —, e ela vale aqui por
// um motivo que não é estética: o despojo é o que **enche a mochila**. Depois de uma tarde
// de caça o jogador tem vinte quadrados de matéria-prima e precisa decidir o que largar no
// chão para caber o resto; com vinte bolas de matiz sorteado ele larga a errada.

const APPLE = '#c8323c';
const APPLE_DARK = '#8a1f26';
const APPLE_LIGHT = '#e2646a';
const LEAF = '#5fa347';
const LEAF_DARK = '#3d7030';

/** Maçã: esfera com covinha no topo, cabinho e folha. A única fruta redonda daqui. */
function drawApple(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Dois lóbulos que se encontram numa covinha: a esfera lisa lê como bola de gude, e a
  // fenda de dois pixels no alto é o que a faz virar fruta.
  ellipse(ctx, cx - 4, cy + 2, 8, 9, APPLE);
  ellipse(ctx, cx + 4, cy + 2, 8, 9, APPLE);
  ellipse(ctx, cx - 5, cy - 1, 4.5, 4, APPLE_LIGHT);
  ellipse(ctx, cx + 5, cy + 5, 5, 4, APPLE_DARK);
  px(ctx, cx - 1, cy - 7, 3, 2, APPLE_DARK);

  px(ctx, cx, cy - 12, 2, 6, '#6b4a2a');
  ctx.fillStyle = LEAF;
  ctx.beginPath();
  ctx.moveTo(cx + 2, cy - 10);
  ctx.quadraticCurveTo(cx + 10, cy - 13, cx + 9, cy - 7);
  ctx.quadraticCurveTo(cx + 5, cy - 6, cx + 2, cy - 10);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx + 4, cy - 10, 4, 1, LEAF_DARK);
}

const CARROT = '#e0761f';
const CARROT_DARK = '#a44d10';
const CARROT_LIGHT = '#f0a04e';

/** Cenoura: cone de ponta para baixo com a rama em cima. O único triângulo invertido. */
function drawCarrot(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = CARROT;
  ctx.beginPath();
  ctx.moveTo(cx - 6, cy - 6);
  ctx.lineTo(cx + 6, cy - 6);
  ctx.lineTo(cx + 1, cy + 13);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = CARROT_LIGHT;
  ctx.beginPath();
  ctx.moveTo(cx - 6, cy - 6);
  ctx.lineTo(cx - 2, cy - 6);
  ctx.lineTo(cx - 1, cy + 5);
  ctx.closePath();
  ctx.fill();

  // Sulcos transversais: sem eles o cone é um chapéu de festa laranja.
  for (const [y, w] of [
    [-3, 9],
    [1, 7],
    [5, 5],
  ] as const) {
    px(ctx, cx - Math.floor(w / 2) + 1, cy + y, w, 1, CARROT_DARK);
  }

  // Rama: três hastes abrindo em leque a partir do ombro.
  for (const [dx, dy] of [
    [-5, -13],
    [0, -15],
    [5, -13],
  ] as const) {
    ctx.strokeStyle = LEAF;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 6);
    ctx.lineTo(cx + dx, cy + dy);
    ctx.stroke();
    px(ctx, cx + dx - 1, cy + dy - 1, 3, 3, LEAF_DARK);
  }
}

/** Ovo cozido: metade em pé, com a gema exposta. Inteiro seria uma pedra branca. */
function drawEgg(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Oval alto e assimétrico — mais estreito em cima —, que é o que separa o ovo do pão.
  ctx.fillStyle = '#f6f2e4';
  ctx.beginPath();
  ctx.ellipse(cx, cy + 1, 8.5, 11.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#dcd5c0';
  ctx.beginPath();
  ctx.ellipse(cx + 3, cy + 3, 5.5, 9, 0, 0, Math.PI * 2);
  ctx.fill();
  ellipse(ctx, cx - 3, cy - 4, 3.5, 4.5, '#fffdf5');

  // O corte: face plana no alto e a gema no meio dela.
  px(ctx, cx - 8, cy - 6, 17, 3, '#fffdf5');
  ellipse(ctx, cx, cy - 5, 4, 2.6, '#f2c33a');
  ellipse(ctx, cx - 1, cy - 6, 1.6, 1, '#fbe89a');
}

const GRAPE = '#7a4ba8';
const GRAPE_DARK = '#4d2c72';
const GRAPE_LIGHT = '#a67fd4';

/** Uvas: cacho em triângulo de bagos redondos. Muitas formas pequenas, e só ele é assim. */
function drawGrapes(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  px(ctx, cx + 1, cy - 13, 2, 5, '#6b4a2a');
  ctx.fillStyle = LEAF;
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 11);
  ctx.quadraticCurveTo(cx - 8, cy - 14, cx - 7, cy - 8);
  ctx.quadraticCurveTo(cx - 3, cy - 7, cx + 1, cy - 11);
  ctx.closePath();
  ctx.fill();

  // Quatro fiadas que estreitam: é o afunilamento que faz o aglomerado virar cacho.
  const rows = [
    [-4, [-6, -2, 2, 6]],
    [1, [-4, 0, 4]],
    [6, [-2, 2]],
    [10, [0]],
  ] as const;

  for (const [dy, columns] of rows) {
    for (const dx of columns) {
      ellipse(ctx, cx + dx, cy + dy, 3.2, 3.2, GRAPE);
      ellipse(ctx, cx + dx + 1, cy + dy + 1, 2.2, 2.2, GRAPE_DARK);
      ellipse(ctx, cx + dx - 1, cy + dy - 1, 1.2, 1.2, GRAPE_LIGHT);
    }
  }
}

/** Sopa quente: tigela funda, caldo até a borda e o vapor subindo. O único côncavo daqui. */
function drawSoup(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Vapor primeiro, para a tigela passar por cima da base dele.
  ctx.strokeStyle = '#cfd9e4aa';
  ctx.lineWidth = 2;
  for (const dx of [-5, 0, 5]) {
    ctx.beginPath();
    ctx.moveTo(cx + dx, cy - 2);
    ctx.quadraticCurveTo(cx + dx - 3, cy - 7, cx + dx, cy - 11);
    ctx.quadraticCurveTo(cx + dx + 3, cy - 14, cx + dx, cy - 16);
    ctx.stroke();
  }

  // A tigela: meia elipse, larga em cima. É o único recipiente aberto do catálogo, e é a
  // abertura que a separa do pão e da carne.
  ctx.fillStyle = '#b8613a';
  ctx.beginPath();
  ctx.ellipse(cx, cy + 1, 13, 11, 0, 0, Math.PI);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#8c4426';
  ctx.beginPath();
  ctx.ellipse(cx + 4, cy + 1, 9, 10, 0, 0, Math.PI);
  ctx.closePath();
  ctx.fill();

  // Caldo, visto de cima pela abertura, com dois pedaços boiando.
  ellipse(ctx, cx, cy + 1, 13, 4, '#7a3a20');
  ellipse(ctx, cx, cy + 1, 11.5, 3, '#c8862c');
  ellipse(ctx, cx - 4, cy, 3, 1.4, '#e0ac52');
  px(ctx, cx + 2, cy, 3, 2, '#8e4a2c');
  px(ctx, cx - 7, cy + 2, 3, 1, '#6f9a3e');

  // Pé da tigela: sem ele o objeto flutua.
  px(ctx, cx - 5, cy + 11, 11, 2, '#6d3319');
}

/** Torta de carne: disco alto com treliça na tampa e uma fatia faltando. */
function drawPie(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Forma de assar por baixo, com a borda crespa passando do disco.
  ellipse(ctx, cx, cy + 5, 13, 6, '#8a5420');
  px(ctx, cx - 13, cy - 1, 27, 6, '#8a5420');
  for (let i = 0; i < 7; i++) px(ctx, cx - 12 + i * 4, cy - 2, 2, 3, '#b9762f');

  // Tampa: disco levemente em perspectiva, mais claro do lado da luz.
  ellipse(ctx, cx, cy - 2, 13, 8, '#d09246');
  ellipse(ctx, cx - 3, cy - 4, 8, 4, '#eab475');

  // Treliça: duas famílias de tiras cruzadas, e é ela que faz o disco virar torta em vez de
  // pão redondo — que é o outro assado do catálogo.
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy - 2, 12, 7, 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.strokeStyle = '#a7661f';
  ctx.lineWidth = 2;
  for (let i = -3; i <= 3; i++) {
    ctx.beginPath();
    ctx.moveTo(cx + i * 5 - 9, cy - 11);
    ctx.lineTo(cx + i * 5 + 9, cy + 7);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + i * 5 + 9, cy - 11);
    ctx.lineTo(cx + i * 5 - 9, cy + 7);
    ctx.stroke();
  }
  ctx.restore();

  // A fatia que falta: a cunha escura é o que diz "comida", e não "objeto redondo".
  ctx.fillStyle = '#5e2f14';
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 2);
  ctx.lineTo(cx + 13, cy - 5);
  ctx.lineTo(cx + 12, cy + 2);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx + 4, cy - 2, 6, 2, '#bf4038');
}

/**
 * Casco de javali: as duas unhas partidas, vistas de frente, com o tufo de pelo por cima.
 *
 * A primeira versão era um trapézio com três espetos no alto e lia como torre de castelo —
 * o erro clássico de desenhar a **descrição** do objeto em vez da silhueta dele. O que faz
 * um casco ser casco é a fenda: duas metades que se tocam e se separam embaixo. Por isso ele
 * é desenhado como duas peças, e não como um bloco com um risco no meio.
 */
function drawHoof(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // **De perfil**, e essa é a decisão inteira. De frente, o casco é um tronco simétrico que
  // alarga para baixo — e tudo o que alarga para baixo e é simétrico lê como vaso, que foi o
  // que as duas versões anteriores desenharam. De lado ele tem um gesto: o talão curto atrás,
  // a pinça comprida à frente, e a sola apoiada no chão. Gesto não se confunde com recipiente.
  ctx.fillStyle = '#4a4038';
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy - 3);
  ctx.lineTo(cx + 1, cy - 6);
  ctx.quadraticCurveTo(cx + 11, cy - 2, cx + 10, cy + 8);
  ctx.lineTo(cx - 8, cy + 8);
  ctx.closePath();
  ctx.fill();

  // A parede da frente pega a luz; o talão fica na sombra.
  ctx.fillStyle = '#6e6055';
  ctx.beginPath();
  ctx.moveTo(cx - 1, cy - 5);
  ctx.quadraticCurveTo(cx + 9, cy - 1, cx + 8, cy + 8);
  ctx.lineTo(cx + 3, cy + 8);
  ctx.quadraticCurveTo(cx + 4, cy, cx - 1, cy - 5);
  ctx.closePath();
  ctx.fill();

  // Sola e a fenda subindo dela: é a fenda que diz "javali", e não "cavalo".
  px(ctx, cx - 9, cy + 8, 20, 3, '#241f1a');
  px(ctx, cx + 4, cy + 1, 2, 8, '#2e2721');

  // Nada de estrias horizontais na parede. Elas são fiéis ao objeto e **erradas no ícone**:
  // três linhas paralelas atravessando um corpo que alarga para baixo desenham aduelas, e o
  // que se lia era um barril com tampa de pelo. A parede fica lisa, e a leitura passa a vir
  // toda da silhueta — pescoço estreito em cima, casco largo embaixo.

  // O pescoço do jarrete, estreito, saindo por trás da coroa. É o contraste entre ele e a
  // base larga que faz o objeto ser uma pata cortada, e não um recipiente.
  px(ctx, cx - 6, cy - 14, 9, 9, '#6f6152');
  px(ctx, cx - 6, cy - 14, 3, 9, '#8a7a63');
  px(ctx, cx - 6, cy - 14, 9, 1, '#39312a');

  // Pelo da coroa: lóbulos que transbordam do pescoço sobre a parede do casco.
  ctx.fillStyle = '#8a7a63';
  for (const [dx, dy] of [
    [-8, -5],
    [-4, -6],
    [0, -6],
    [4, -5],
  ] as const) {
    ctx.beginPath();
    ctx.arc(cx + dx, cy + dy, 3, Math.PI * 0.85, Math.PI * 2.15);
    ctx.fill();
  }
  px(ctx, cx - 9, cy - 5, 15, 2, '#8a7a63');
  px(ctx, cx - 9, cy - 5, 7, 1, '#a8977c');
}

/** Seda de aranha: novelo de fio claro enrolado num carretel, com a ponta solta. */
function drawSilk(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ellipse(ctx, cx, cy + 1, 10, 9, '#c3cedd');
  ellipse(ctx, cx - 2, cy - 1, 8, 6.5, '#dfe6f0');

  // Voltas do fio: arcos concêntricos deslocados, que é o que faz a bola virar novelo.
  ctx.strokeStyle = '#a9b6c6';
  ctx.lineWidth = 1;
  for (const [rx, ry, rot] of [
    [8, 4, 0.3],
    [6, 3, -0.5],
    [9, 3, 1.1],
  ] as const) {
    ctx.beginPath();
    ctx.ellipse(cx, cy + 1, rx, ry, rot, 0, Math.PI * 2);
    ctx.stroke();
  }

  // A ponta solta, saindo por cima: sem ela o novelo é uma pedra listrada.
  ctx.strokeStyle = '#eef3fa';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx + 6, cy - 6);
  ctx.quadraticCurveTo(cx + 12, cy - 12, cx + 4, cy - 13);
  ctx.stroke();
}

/** Asa de morcego: membrana em três dedos, com as falanges escuras por dentro. */
function drawBatWing(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Recortes côncavos entre os dedos: é a borda em festão que diz "asa de morcego" e não
  // "folha". Uma membrana de borda lisa cai na mesma leitura da Asa de vespa.
  ctx.fillStyle = '#6b4a6e';
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy - 10);
  ctx.lineTo(cx + 9, cy - 6);
  ctx.quadraticCurveTo(cx + 3, cy - 1, cx + 8, cy + 3);
  ctx.quadraticCurveTo(cx + 1, cy + 5, cx + 5, cy + 11);
  ctx.quadraticCurveTo(cx - 4, cy + 8, cx - 10, cy + 11);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#4a2f4e';
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy + 11);
  ctx.quadraticCurveTo(cx - 2, cy + 6, cx + 5, cy + 11);
  ctx.quadraticCurveTo(cx - 3, cy + 9, cx - 10, cy + 11);
  ctx.closePath();
  ctx.fill();

  // Falanges: o osso comprido na borda de ataque e os dois dedos que abrem a membrana.
  ctx.strokeStyle = '#d8cbb8';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy - 10);
  ctx.lineTo(cx + 9, cy - 6);
  ctx.stroke();
  ctx.lineWidth = 1;
  for (const [x, y] of [
    [8, 3],
    [5, 11],
  ] as const) {
    ctx.beginPath();
    ctx.moveTo(cx - 8, cy - 9);
    ctx.lineTo(cx + x, cy + y);
    ctx.stroke();
  }
  px(ctx, cx - 12, cy - 12, 4, 4, '#d8cbb8');
}

/** Crânio: calota, órbitas fundas e a arcada. A silhueta que o jogo inteiro reconhece. */
function drawSkull(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ellipse(ctx, cx, cy - 3, 10, 9, BONE);
  ellipse(ctx, cx - 4, cy - 7, 5, 3.5, '#fbf6e8');
  px(ctx, cx - 6, cy + 3, 13, 5, BONE);
  px(ctx, cx + 3, cy - 6, 4, 12, BONE_DARK);

  // Órbitas: dois vazios grandes e redondos. É o tamanho delas que faz o crânio ser crânio
  // — órbita pequena lê como ovo com dois furos.
  ellipse(ctx, cx - 4, cy - 3, 3.4, 3.8, '#241f1a');
  ellipse(ctx, cx + 4, cy - 3, 3.4, 3.8, '#241f1a');
  ellipse(ctx, cx - 4.5, cy - 3.5, 1.2, 1.4, '#5a5148');
  px(ctx, cx - 1, cy + 1, 3, 3, '#241f1a');

  // Arcada: os dentes separados por sulcos claros, e o maxilar por baixo.
  px(ctx, cx - 6, cy + 6, 13, 4, '#f6f0dd');
  for (const dx of [-4, -1, 2, 5]) px(ctx, cx + dx, cy + 6, 1, 4, '#a89b80');
  px(ctx, cx - 6, cy + 9, 13, 2, BONE_DARK);
}

interface Essence {
  readonly core: string;
  readonly glow: string;
  readonly dark: string;
}

// As cores são as que a magia do elemento já pinta na tela (`art/effects.ts`), e não uma
// paleta nova: o jogador aprendeu o vocabulário apanhando de cada uma delas.
const ESSENCE_EARTH: Essence = { core: '#8fae57', glow: '#c2dd91', dark: '#4a6d43' };
const ESSENCE_FIRE: Essence = { core: '#e04a20', glow: '#ffd166', dark: '#8c2a10' };
const ESSENCE_ICE: Essence = { core: '#4fa3d9', glow: '#bfe9ff', dark: '#265d85' };
const ESSENCE_ENERGY: Essence = { core: '#7f5af0', glow: '#d6c6ff', dark: '#422c8e' };

/**
 * Essência elemental: cristal alongado, faceta clara de um lado e brilho no miolo.
 *
 * As quatro dividem a forma de propósito — é o que faz o jogador aprender "isto é essência"
 * uma vez e reconhecer as outras três sem ler. O que as separa é a cor, e aqui isso é
 * suficiente porque a cor **significa**: são as mesmas quatro do efeito de magia.
 */
function drawEssence(ctx: CanvasRenderingContext2D, cx: number, cy: number, essence: Essence): void {
  const shard = (): void => {
    ctx.beginPath();
    ctx.moveTo(cx, cy - 14);
    ctx.lineTo(cx + 7, cy - 4);
    ctx.lineTo(cx + 4, cy + 12);
    ctx.lineTo(cx - 4, cy + 12);
    ctx.lineTo(cx - 7, cy - 4);
    ctx.closePath();
  };

  ctx.fillStyle = essence.dark;
  shard();
  ctx.fill();

  // Faceta iluminada: metade do cristal, cortada pelo eixo. Duas cores num corpo só é o que
  // dá volume a uma pedra sem contorno interno.
  ctx.save();
  shard();
  ctx.clip();
  ctx.fillStyle = essence.core;
  ctx.beginPath();
  ctx.moveTo(cx, cy - 14);
  ctx.lineTo(cx - 7, cy - 4);
  ctx.lineTo(cx - 4, cy + 12);
  ctx.lineTo(cx, cy + 12);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // O miolo aceso e o reflexo: é o que separa "cristal" de "pedra lascada".
  ellipse(ctx, cx, cy + 1, 2.6, 5, essence.glow);
  px(ctx, cx - 4, cy - 6, 2, 7, essence.glow);
  px(ctx, cx - 5, cy - 4, 1, 4, '#ffffffaa');
}

/**
 * Escama: três placas sobrepostas, a da frente inteira e duas assomando por trás.
 *
 * Uma escama sozinha, com quilha vertical e ponta em cima, é uma **folha** — foi o que a
 * primeira versão desenhou. O que diz "escama" não é o contorno de uma: é a sobreposição de
 * várias, que é a mesma razão pela qual a Placa de escamas (103) desenha fiadas em vez de
 * pintar o peitoral de verde. Aqui a peça é solta, então a sobreposição é de três, e a
 * silhueta larga e arredondada embaixo termina de matar a leitura de folha.
 */
function drawScale(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  base: string,
  dark: string,
  light: string,
): void {
  // Uma peça só, e o desenho interno é que diz "escama".
  //
  // A tentativa de compor três placas sobrepostas — que é o que a Placa de escamas (103) faz
  // — devolveu um **coração**: dois lóbulos em cima e uma ponta embaixo é exatamente a forma
  // que o Coração de troll já ocupa no catálogo, e dois itens não podem dividir silhueta. A
  // saída foi o contrário: contorno único, com o topo quase reto, e a leitura vindo dos
  // **anéis horizontais** por dentro. Anel horizontal é o que uma escama tem e uma folha não
  // — a folha tem nervura vertical, que foi o defeito da primeira versão.
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy - 6);
  ctx.quadraticCurveTo(cx, cy - 11, cx + 10, cy - 6);
  ctx.quadraticCurveTo(cx + 9, cy + 5, cx, cy + 13);
  ctx.quadraticCurveTo(cx - 9, cy + 5, cx - 10, cy - 6);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = base;
  ctx.beginPath();
  ctx.moveTo(cx - 8, cy - 5);
  ctx.quadraticCurveTo(cx, cy - 9, cx + 8, cy - 5);
  ctx.quadraticCurveTo(cx + 7, cy + 4, cx, cy + 10);
  ctx.quadraticCurveTo(cx - 7, cy + 4, cx - 8, cy - 5);
  ctx.closePath();
  ctx.fill();

  // Anéis de crescimento: três arcos que acompanham a borda de cima e vão estreitando.
  ctx.strokeStyle = dark;
  ctx.lineWidth = 1;
  for (const [dy, w] of [
    [-1, 7],
    [3, 5.5],
    [6, 3.5],
  ] as const) {
    ctx.beginPath();
    ctx.moveTo(cx - w, cy + dy);
    ctx.quadraticCurveTo(cx, cy + dy - 2.5, cx + w, cy + dy);
    ctx.stroke();
  }

  // Rebordo aceso na quina superior esquerda, de onde vem a luz do noroeste.
  ctx.fillStyle = light;
  ctx.beginPath();
  ctx.moveTo(cx - 8, cy - 5);
  ctx.quadraticCurveTo(cx - 3, cy - 8, cx + 1, cy - 8);
  ctx.quadraticCurveTo(cx - 3, cy - 6, cx - 6, cy + 1);
  ctx.closePath();
  ctx.fill();
}

/** Coração de troll: dois lóbulos, a aorta cortada e os vasos por cima. Nada mais é assim. */
function drawHeart(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = '#8e2b2f';
  ctx.beginPath();
  ctx.moveTo(cx, cy + 13);
  ctx.quadraticCurveTo(cx - 13, cy + 2, cx - 11, cy - 6);
  ctx.quadraticCurveTo(cx - 8, cy - 12, cx, cy - 5);
  ctx.quadraticCurveTo(cx + 8, cy - 12, cx + 11, cy - 6);
  ctx.quadraticCurveTo(cx + 13, cy + 2, cx, cy + 13);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#b8474a';
  ctx.beginPath();
  ctx.moveTo(cx - 2, cy + 6);
  ctx.quadraticCurveTo(cx - 11, cy, cx - 9, cy - 5);
  ctx.quadraticCurveTo(cx - 6, cy - 9, cx - 1, cy - 4);
  ctx.closePath();
  ctx.fill();
  ellipse(ctx, cx - 6, cy - 3, 2.4, 2, '#d9787a');

  // Vasos: três riscos escuros que descem da fenda. É o detalhe que impede a forma de ser
  // lida como o coração de baralho.
  ctx.strokeStyle = '#5e1a1e';
  ctx.lineWidth = 1;
  for (const [dx, dy] of [
    [-4, 4],
    [3, 5],
    [6, 1],
  ] as const) {
    ctx.beginPath();
    ctx.moveTo(cx, cy - 4);
    ctx.quadraticCurveTo(cx + dx, cy + dy - 4, cx + dx, cy + dy + 2);
    ctx.stroke();
  }

  // Aorta cortada, saindo do alto.
  px(ctx, cx - 3, cy - 13, 6, 6, '#a8595b');
  px(ctx, cx - 3, cy - 13, 6, 2, '#5e1a1e');
}

/**
 * Poeira espectral: o frasco de vidro deitado e a nuvem que escapa dele.
 *
 * Era um saco redondo com um fio subindo do nó, e a 32 px isso é uma bomba de desenho
 * animado. O conserto não foi mexer no nó: foi trocar o recipiente. Um vidro **deitado**,
 * derramando, é a única forma daqui que diz "isto é pó" — e o eixo horizontal ainda o separa
 * das poções, que são todas verticais.
 */
function drawDust(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // A nuvem primeiro, por baixo do vidro: lóbulos soltos subindo à direita da boca.
  for (const [dx, dy, r] of [
    [4, -2, 5],
    [8, -6, 4],
    [5, -9, 3],
    [10, -11, 2.2],
    [1, -6, 2.6],
  ] as const) {
    ellipse(ctx, cx + dx, cy + dy, r, r * 0.85, '#6a5ba8');
    ellipse(ctx, cx + dx - 1, cy + dy - 1, r * 0.55, r * 0.45, '#a08fd8');
  }

  // Motas: pixels claros soltos dentro e acima da nuvem.
  for (const [dx, dy] of [
    [3, -12],
    [9, -3],
    [12, -8],
    [6, -14],
    [0, -9],
  ] as const) {
    px(ctx, cx + dx, cy + dy, 1, 1, '#eee6ff');
  }

  // O vidro, deitado com o fundo à esquerda e a boca apontando para a nuvem.
  ctx.fillStyle = '#c9d3e0';
  ctx.beginPath();
  ctx.moveTo(cx - 12, cy + 2);
  ctx.lineTo(cx - 12, cy + 10);
  ctx.lineTo(cx + 1, cy + 10);
  ctx.lineTo(cx + 4, cy + 7);
  ctx.lineTo(cx + 4, cy + 4);
  ctx.lineTo(cx + 1, cy + 2);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx - 12, cy + 2, 13, 2, '#eef3fa');
  px(ctx, cx - 12, cy + 8, 13, 2, '#8f9cad');
  // O resto do pó no fundo do vidro, e a rolha solta ao lado.
  px(ctx, cx - 10, cy + 5, 8, 4, '#6a5ba8');
  px(ctx, cx + 4, cy + 3, 3, 5, '#9a7a4a');
}

/**
 * Fragmento do Alvorecer: o caco de luz que prova que a classe celestial existe (PRD §15.11).
 *
 * O único item do catálogo cuja função é ser **visto**. Ele cai cem vezes mais que as peças
 * do Alvorecer justamente para que alguém, um dia, mostre um no chat — e saía como a mesma
 * bolha de matiz sorteado que uma asa de morcego. O desenho é o oposto de todos os outros
 * daqui: sem contorno de matéria, sem sombra do noroeste, raios em vez de volume. Ele não é
 * um objeto iluminado; ele é a luz.
 */
function drawDawnShard(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Raios primeiro, por baixo do caco.
  ctx.fillStyle = '#ffe9a855';
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const reach = i % 2 === 0 ? 15 : 10;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle - 0.13) * 4, cy + Math.sin(angle - 0.13) * 4);
    ctx.lineTo(cx + Math.cos(angle) * reach, cy + Math.sin(angle) * reach);
    ctx.lineTo(cx + Math.cos(angle + 0.13) * 4, cy + Math.sin(angle + 0.13) * 4);
    ctx.closePath();
    ctx.fill();
  }

  // O caco: losango irregular, mais alto que largo, com a face esquerda em branco puro.
  ctx.fillStyle = '#e6c766';
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 12);
  ctx.lineTo(cx + 7, cy - 2);
  ctx.lineTo(cx + 2, cy + 12);
  ctx.lineTo(cx - 3, cy + 9);
  ctx.lineTo(cx - 7, cy - 3);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#fffdf0';
  ctx.beginPath();
  ctx.moveTo(cx + 1, cy - 12);
  ctx.lineTo(cx - 7, cy - 3);
  ctx.lineTo(cx - 3, cy + 9);
  ctx.lineTo(cx, cy + 6);
  ctx.closePath();
  ctx.fill();

  px(ctx, cx + 2, cy - 6, 2, 10, '#c9a24a');
  ellipse(ctx, cx - 1, cy - 4, 2, 3.5, '#ffffff');
}

/**
 * Contorno escuro em volta do ícone.
 *
 * A varredura mora em `canvas.ts` porque a arte de criatura passou a usar a mesma: sem ele
 * um ícone escuro some no fundo do inventário, e um bicho escuro some na grama.
 */
function outline(ctx: CanvasRenderingContext2D): void {
  strokeSilhouette(ctx, OUTLINE);
}

/** Cor do contorno, exposta para quem desenha moldura de slot na mesma paleta. */
export const ITEM_OUTLINE = OUTLINE;
